import { S } from '../core/game';
import type { Decor, Rect } from '../world/mapdef';
import { XM_SHOPS, XM_VSIGNS, type XmShop } from '../world/objectsXimen';
import { OUTLINE, block, ellipse, hash, rect, shadow, shade, sun, type Ctx } from './paint';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Facing } from './sprites';

/**
 * Prop drawers of the ximen package: Ximending (neon facades, giant screens, cinemas, the Red
 * House, Exit 6, food stalls, buskers) and Longshan Temple with old Wanhua (swallowtail roofs,
 * bronze dragon pillars, the incense burner, Herb Alley, Bopiliao, the Huaxi gate, Bangka Park).
 * Big static pieces are rendered once into bitmaps (day and night versions) and blitted.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

// ---------------------------------------------------------------------------
// Helpers (same look as props4 / propsHualien)
// ---------------------------------------------------------------------------

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

/** Text squeezed to fit `maxW`. */
function fit(c: Ctx, s: string, x: number, y: number, size: number, col: string, maxW: number, shadowCol?: string): void {
  c.font = `900 ${size}px ${CJK}`;
  const w = c.measureText(s).width;
  const k = w > maxW ? maxW / w : 1;
  c.save();
  c.translate(x, y);
  c.scale(k, 1);
  txt(c, s, 0, 0, size, col, 900, shadowCol);
  c.restore();
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const isNight = () => sun.alpha < 0.8;
const now = () => S().time;

/** Static art rendered once per zoom level into a bitmap; a small LRU keeps memory bounded. */
const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.max(1, Math.min(5, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    if (bitmaps.size > 70) bitmaps.delete(bitmaps.keys().next().value!);
  } else bitmaps.delete(key);
  bitmaps.set(key, e);
  c.drawImage(e.cv, x0, y0, w, h);
}

function steam(c: Ctx, x: number, y: number, t: number, n = 3, a = 0.35): void {
  for (let i = 0; i < n; i++) {
    const k = (t * 0.6 + i / n) % 1;
    ellipse(c, `rgba(255,255,255,${a * (1 - k)})`, x + Math.sin(t * 2 + i * 2) * 2, y - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
}

function smoke(c: Ctx, x: number, y: number, t: number, n: number, rise: number, a: number, spread = 1): void {
  for (let i = 0; i < n; i++) {
    const k = (t * 0.25 + i / n) % 1;
    const sx = x + Math.sin(t * 0.8 + i * 1.7) * (2 + k * 8) * spread;
    ellipse(c, `rgba(235,232,228,${a * (1 - k) * Math.min(1, k * 6)})`, sx, y - k * rise, 2 + k * 7 * spread, 1.6 + k * 4 * spread);
  }
}

/** A red paper lantern, `r` wide. */
function lantern(c: Ctx, x: number, y: number, r: number, night: boolean, col = '#d8282a', label?: string): void {
  rect(c, '#2a1810', x - 0.3, y - r * 1.4, 0.6, r * 0.6);
  rect(c, '#1b1410', x - r * 0.45, y - r * 0.85, r * 0.9, r * 0.25);
  ellipse(c, night ? shade(col, 0.25) : col, x, y, r, r * 0.78);
  ellipse(c, 'rgba(255,255,255,0.18)', x - r * 0.35, y - r * 0.25, r * 0.25, r * 0.4);
  for (const dx of [-0.5, 0, 0.5]) rect(c, 'rgba(0,0,0,0.12)', x + dx * r - 0.2, y - r * 0.7, 0.4, r * 1.4);
  rect(c, '#1b1410', x - r * 0.45, y + r * 0.62, r * 0.9, r * 0.25);
  rect(c, '#e8b830', x - 0.3, y + r * 0.85, 0.6, r * 0.5);
  if (label) txt(c, label, x, y + 0.2, r * 0.8, '#ffe08a', 900);
  if (night) ellipse(c, 'rgba(255,120,60,0.22)', x, y, r * 2.4, r * 2);
}

/** A night-market stall (same look as props3/props4): counter, striped canopy, glowing sign, bulb. */
function stall(c: Ctx, r: Rect, t: number, label: string, canopy: string, goods: (c: Ctx, r: Rect, t: number) => void, open = true): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3.5, 0.35);
  rect(c, OUTLINE, x + 1, y - 34, 2, 36);
  rect(c, OUTLINE, x + w - 3, y - 34, 2, 36);
  const top = y - 40;
  for (let i = 0; i < w; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 9);
    c.beginPath();
    c.arc(x + 1 + i, top + 9, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.25)', x - 2, top + 9, w + 4, 1);
  block(c, x + w / 2 - Math.max(14, label.length * 4.5 + 4), top - 11, Math.max(28, label.length * 9 + 8), 11, '#c0392b', '#e05a48', '#8a2a1e');
  txt(c, label, x + w / 2, top - 5.1, 8, '#fff6dc', 900, 'rgba(0,0,0,0.35)');
  if (open) {
    const sw = Math.sin(t * 1.7 + x) * 0.6;
    line(c, OUTLINE, 0.6, [x + w / 2, top + 9, x + w / 2 + sw, top + 16]);
    ellipse(c, '#fff3b0', x + w / 2 + sw, top + 17.5, 1.8, 2);
    ellipse(c, 'rgba(255,230,140,0.25)', x + w / 2 + sw, top + 18, 6, 5);
    goods(c, r, t);
  }
  block(c, x, y - 8, w, h + 8, '#9a6a44', '#b88a5c', '#6e4a2e');
  rect(c, '#f2e6cf', x + 1, y - 9, w - 2, 2);
  for (let i = 4; i < w - 2; i += 8) rect(c, 'rgba(0,0,0,0.15)', x + i, y - 6, 0.6, h + 4);
  if (!open) {
    // Rolled-up tarp: closed until the evening.
    block(c, x + 1, y - 30, w - 2, 21, '#4a6a8a', '#5a7a9a', '#3a5a7a');
    for (let i = x + 4; i < x + w - 2; i += 5) rect(c, 'rgba(0,0,0,0.12)', i, y - 29, 0.5, 19);
  }
}

// ---------------------------------------------------------------------------
// People (crowds, worshippers, eaters) drawn with the game's character renderer
// ---------------------------------------------------------------------------

const SKINS = ['#f6cfa8', '#f3c9a0', '#e8b48c', '#d9a47a', '#c88a5a', '#f8d8bc'];
const HAIRS = ['#1d1a22', '#2a1d16', '#6b3f22', '#a8432a', '#e2b45a', '#9a948a', '#c9c4bc', '#3a2418'];
const TOPS: [string, string][] = [['#ff6aa0', '#d04a80'], ['#3a8fd9', '#2a6fb0'], ['#f4f1ea', '#cfc8bb'], ['#ffd34e', '#d8b030'], ['#1b1d24', '#0e1014'], ['#57c278', '#3a9a58'], ['#9b5fc0', '#7a3fa0'], ['#e2493b', '#b0302a'], ['#ff9f43', '#d07a2a'], ['#7ad0e0', '#4aa0b0']];
const STYLES = ['short', 'long', 'pony', 'bun', 'cap', 'pigtails', 'messy', 'neat', 'curly'] as const;
const LOOKS: CharLook[] = Array.from({ length: 16 }, (_, i) => {
  const elder = i % 5 === 4;
  const [top, topShade] = TOPS[(i * 3) % TOPS.length];
  return {
    skin: SKINS[i % SKINS.length],
    hair: elder ? HAIRS[5 + (i % 2)] : HAIRS[(i * 5) % 5],
    hairStyle: STYLES[(i * 7) % STYLES.length],
    hat: ['#e2493b', '#1b1d24', '#3a8fd9', '#ffffff'][i % 4],
    top,
    topShade,
    bottom: ['#2f3442', '#3a4a7a', '#1d1a22', '#7a5a3a', '#4a6a8a'][i % 5],
    shoes: ['#f4f1ea', '#1d1814', '#e2493b'][i % 3],
    dress: i % 3 === 1,
    glasses: i % 6 === 2,
    small: i % 7 === 3,
  };
});
const FACINGS: Facing[] = ['down', 'up', 'left', 'right'];

/** `variant`: look*4 + facing; +64 holding incense; +128 doing tai chi. */
function person(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const mode = v >= 128 ? 2 : v >= 64 ? 1 : 0;
  const base = v % 64;
  const look = LOOKS[Math.floor(base / 4) % LOOKS.length];
  const seed = hash(Math.round(d.x), Math.round(d.y));
  let facing = FACINGS[base % 4];
  if (mode === 2) facing = FACINGS[[0, 2, 0, 3][Math.floor(t / 2.5 + seed * 4) % 4]];
  const breath = (t * 0.45 + seed) % 1;
  // Someone checking their phone now and then.
  drawCharacter(c, look, d.x, d.y, { facing, phase: 0, moving: false, blink: (t + seed * 7) % 4 < 0.12, breath });
  if (mode === 1) {
    // Three incense sticks held up in both hands (seen above the head from behind), with smoke.
    const bow = Math.max(0, Math.sin(t * 1.2 + seed * 6)) * 2;
    const hx = d.x, hy = facing === 'up' ? d.y - 25 + bow : d.y - 13 + bow;
    const len = facing === 'up' ? 6 : 10;
    for (const dx of [-1, 0, 1]) line(c, '#c0392b', 0.5, [hx + dx * 0.6, hy, hx + dx * 1.2, hy - len]);
    for (const dx of [-1, 0, 1]) ellipse(c, '#ff7a3a', hx + dx * 1.2, hy - len - 0.2, 0.5, 0.5);
    for (let i = 0; i < 2; i++) {
      const k = (t * 0.5 + i * 0.5 + seed) % 1;
      ellipse(c, `rgba(240,240,240,${0.35 * (1 - k)})`, hx + Math.sin(t * 2 + i) * 1.5, hy - len - 1 - k * 10, 0.8 + k, 0.8 + k);
    }
  } else if (mode === 0 && seed > 0.62 && facing === 'down') {
    // Phone glow.
    block(c, d.x + 1.4, d.y - 12, 2.6, 3.6, '#1b1d24');
    rect(c, isNight() ? '#bfe6ff' : '#7ab0d0', d.x + 1.9, d.y - 11.4, 1.6, 2.4);
  }
}

// ---------------------------------------------------------------------------
// Shop facades
// ---------------------------------------------------------------------------

type Goods = 'clothes' | 'sneakers' | 'tea' | 'chicken' | 'ice' | 'records' | 'tattoo' | 'nails' | 'drugstore' | 'sparkle' | 'phone'
  | 'noodle' | 'shoes' | 'sticker' | 'ktv' | 'hotel' | 'books' | 'glasses' | 'anime' | 'mart' | 'bank' | 'coffee' | 'ramen'
  | 'tv' | 'sports' | 'arcade' | 'claw' | 'herbs' | 'drawers' | 'gold' | 'teaJars' | 'buddha' | 'fruit' | 'lanterns' | 'seals' | 'none';
type Style = 'modern' | 'cinema' | 'tower' | 'old' | 'arcade';
interface ShopSpec {
  zh: string;
  en: string;
  wall: string;
  band: string;
  ink: string;
  goods: Goods;
  style?: Style;
  poster?: number;
}

const SHOPS: Record<XmShop, ShopSpec> = {
  cinemaStar: { zh: '星光大戲院', en: 'STARLIGHT CINEMA', wall: '#3a2a4a', band: '#1b1d24', ink: '#ffd34e', goods: 'none', style: 'cinema', poster: 0 },
  records: { zh: '唱片行', en: 'RECORDS', wall: '#d8cfc0', band: '#5a2a8a', ink: '#ffffff', goods: 'records' },
  sneaker: { zh: '球鞋實驗室', en: 'SNEAKER LAB', wall: '#e8e6e2', band: '#1b1d24', ink: '#4ae0ff', goods: 'sneakers' },
  cinemaCentury: { zh: '新世紀影城', en: 'NEW CENTURY', wall: '#5a1a1a', band: '#c0392b', ink: '#fff3b0', goods: 'none', style: 'cinema', poster: 3 },
  pearlTea: { zh: '珍珠樂', en: 'PEARL JOY · BUBBLE TEA', wall: '#f4e2c4', band: '#7a3a1a', ink: '#ffe08a', goods: 'tea' },
  arcade: { zh: '電子遊樂場', en: 'GAME CENTER', wall: '#2a1a4a', band: '#ff2a8a', ink: '#ffffff', goods: 'arcade' },
  claw: { zh: '夾娃娃專賣', en: 'CLAW!', wall: '#ffe0f0', band: '#ff4aa8', ink: '#ffffff', goods: 'claw' },
  cinemaDaily: { zh: '日日劇院', en: 'DAILY THEATRE', wall: '#2a4a5a', band: '#1b1d24', ink: '#ff9a3a', goods: 'none', style: 'cinema', poster: 6 },
  tattoo: { zh: '刺青', en: 'TATTOO STUDIO', wall: '#2a2a2e', band: '#111111', ink: '#ff3a3a', goods: 'tattoo' },
  nails: { zh: '美甲美睫', en: 'NAILS & LASHES', wall: '#fbe4ec', band: '#f07aa8', ink: '#ffffff', goods: 'nails' },
  streetwear: { zh: '潮流服飾', en: 'STREET WEAR', wall: '#cfd6dc', band: '#1b1d24', ink: '#ffffff', goods: 'clothes' },
  drugstore: { zh: '康美藥妝', en: 'KANGMEI DRUGSTORE', wall: '#e8f2ea', band: '#1f8a5a', ink: '#ffffff', goods: 'drugstore' },
  accessories: { zh: '飾品', en: 'ACCESSORIES', wall: '#f2e8f8', band: '#9a5ad0', ink: '#ffffff', goods: 'sparkle' },
  phonecase: { zh: '手機殼', en: 'PHONE CASES', wall: '#e2eef8', band: '#2a8ad0', ink: '#ffffff', goods: 'phone' },
  meesua: { zh: '阿春麵線', en: 'A-CHUN MEE SUA', wall: '#e8d8b8', band: '#c0392b', ink: '#fff3b0', goods: 'noodle' },
  chicken: { zh: '超大雞排', en: 'XL CHICKEN', wall: '#f8e0b0', band: '#e07a1a', ink: '#ffffff', goods: 'chicken' },
  ice: { zh: '雪山冰店', en: 'SNOW MOUNTAIN ICE', wall: '#e0f2fa', band: '#2a8ad0', ink: '#ffffff', goods: 'ice' },
  shoes: { zh: '鞋', en: 'SHOES', wall: '#e8e0d4', band: '#3a3a3e', ink: '#ffd34e', goods: 'shoes' },
  sticker: { zh: '大頭貼', en: 'PHOTO STICKERS', wall: '#ffe8f4', band: '#ff6ab0', ink: '#ffffff', goods: 'sticker' },
  ktv: { zh: '好歌ＫＴＶ', en: 'GOOD SONG KTV', wall: '#3a1a5a', band: '#5a1a8a', ink: '#ffd34e', goods: 'ktv' },
  hotel: { zh: '西門旅店', en: 'XIMEN INN', wall: '#d8d0c4', band: '#1f3a6a', ink: '#ffffff', goods: 'hotel' },
  books: { zh: '書店', en: 'BOOKS', wall: '#e8dcc4', band: '#3a6a3a', ink: '#fff6dc', goods: 'books' },
  glasses: { zh: '眼鏡', en: 'OPTICAL', wall: '#e4ecf2', band: '#2a5a8a', ink: '#ffffff', goods: 'glasses' },
  fashion: { zh: '女裝', en: 'FASHION', wall: '#f8e8e0', band: '#c0306a', ink: '#ffffff', goods: 'clothes' },
  wanxiang: { zh: '萬象大樓', en: 'WANXIANG BUILDING', wall: '#7a8a96', band: '#1b1d24', ink: '#ffffff', goods: 'mart', style: 'tower' },
  coffee: { zh: '咖啡', en: 'COFFEE', wall: '#5a3a2a', band: '#2a1a10', ink: '#e8c890', goods: 'coffee' },
  ramen: { zh: '拉麵', en: 'RAMEN', wall: '#e8d8c0', band: '#1b1d24', ink: '#ff9a3a', goods: 'ramen' },
  electronics: { zh: '電器', en: 'ELECTRONICS', wall: '#d4dce4', band: '#1f6fc0', ink: '#ffffff', goods: 'tv' },
  sports: { zh: '運動用品', en: 'SPORTS', wall: '#e2ece0', band: '#2a8a3a', ink: '#ffffff', goods: 'sports' },
  anime: { zh: '動漫天地', en: 'ANIME WORLD', wall: '#fff0d8', band: '#d0453a', ink: '#ffe08a', goods: 'anime' },
  mart: { zh: '好鄰居', en: 'GOOD NEIGHBOUR 24H', wall: '#f4f4f0', band: '#2a9a5a', ink: '#ffffff', goods: 'mart' },
  bank: { zh: '銀行', en: 'BANK', wall: '#d8d4cc', band: '#1f3a6a', ink: '#ffd34e', goods: 'bank' },
  herbTea: { zh: '青草茶', en: 'HERBAL TEA', wall: '#c8d8b0', band: '#2f7a3a', ink: '#fff6dc', goods: 'herbs', style: 'old' },
  herbShop: { zh: '百草店', en: 'HUNDRED HERBS', wall: '#d8d0b0', band: '#1b1d24', ink: '#e8c860', goods: 'herbs', style: 'old' },
  coolTea: { zh: '涼茶', en: 'COOLING TEA', wall: '#b8d0b8', band: '#3a6a2a', ink: '#ffffff', goods: 'teaJars', style: 'old' },
  medicine: { zh: '中藥行', en: 'CHINESE MEDICINE', wall: '#e0d4bc', band: '#7a2a1a', ink: '#ffd34e', goods: 'drawers', style: 'old' },
  danzai: { zh: '擔仔麵', en: 'DANZAI NOODLES', wall: '#e8dcc4', band: '#c0392b', ink: '#ffffff', goods: 'noodle', style: 'old' },
  joss: { zh: '香燭金紙', en: 'INCENSE & JOSS PAPER', wall: '#e8d8b0', band: '#b8862a', ink: '#7a1a10', goods: 'gold', style: 'old' },
  teaHouse: { zh: '老茶行', en: 'OLD TEA SHOP', wall: '#d0c0a0', band: '#3a2a1a', ink: '#e8c860', goods: 'teaJars', style: 'old' },
  buddhist: { zh: '佛具', en: 'BUDDHIST GOODS', wall: '#e8dcc0', band: '#c8961a', ink: '#7a1a10', goods: 'buddha', style: 'old' },
  medicine2: { zh: '老中藥房', en: 'HERBAL PHARMACY', wall: '#c8d4c8', band: '#7a2a1a', ink: '#ffd34e', goods: 'drawers', style: 'old' },
  fruitIce: { zh: '冰果室', en: 'FRUIT & ICE', wall: '#d0e8f0', band: '#2a8ad0', ink: '#fff3b0', goods: 'fruit', style: 'old' },
  lantern: { zh: '燈籠', en: 'LANTERNS', wall: '#e8d4b8', band: '#c0392b', ink: '#ffd34e', goods: 'lanterns', style: 'old' },
  seal: { zh: '刻印', en: 'SEALS', wall: '#d8ccb8', band: '#1b1d24', ink: '#e8473a', goods: 'seals', style: 'old' },
  oldTea: { zh: '茶', en: 'TEA', wall: '#c8bca0', band: '#3a5a2a', ink: '#fff6dc', goods: 'teaJars', style: 'old' },
  bopiliao1: { zh: '剝皮寮', en: 'BOPILIAO', wall: '#a8452e', band: '#5a2a1a', ink: '#f4e8c8', goods: 'books', style: 'arcade', poster: 0 },
  bopiliao2: { zh: '鄉土教育中心', en: 'HERITAGE CENTRE', wall: '#b04a32', band: '#3a2a1a', ink: '#f4e8c8', goods: 'none', style: 'arcade', poster: 1 },
  bopiliao3: { zh: '老書局', en: 'OLD BOOKSHOP', wall: '#9a4030', band: '#2a3a2a', ink: '#f4e8c8', goods: 'books', style: 'arcade', poster: 2 },
  bopiliao4: { zh: '老照相館', en: 'OLD PHOTO STUDIO', wall: '#a84a34', band: '#3a2a1a', ink: '#f4e8c8', goods: 'none', style: 'arcade', poster: 3 },
};

/** Little goods behind a shop window (gx, gy: top-left, gw × gh). */
function goods(c: Ctx, k: Goods, gx: number, gy: number, gw: number, gh: number, seed: number): void {
  const n = Math.max(2, Math.floor(gw / 7));
  const step = gw / n;
  const pal = ['#ff6aa0', '#3a8fd9', '#ffd34e', '#57c278', '#9b5fc0', '#ff9f43', '#f4f1ea', '#e2493b'];
  const col = (i: number) => pal[Math.floor(hash(i, seed) * pal.length)];
  switch (k) {
    case 'clothes':
      line(c, '#8a9096', 0.6, [gx + 1, gy + 2, gx + gw - 1, gy + 2]);
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        poly(c, col(i), [x - 2.6, gy + 3, x + 2.6, gy + 3, x + 3.4, gy + 5, x + 2.2, gy + 5.4, x + 2.2, gy + gh - 3, x - 2.2, gy + gh - 3, x - 2.2, gy + 5.4, x - 3.4, gy + 5]);
      }
      return;
    case 'sneakers':
    case 'shoes':
      for (let r = 0; r < 2; r++) {
        rect(c, '#c8ccd0', gx + 1, gy + 4 + r * 6, gw - 2, 0.6);
        for (let i = 0; i < n; i++) {
          const x = gx + step * (i + 0.5);
          block(c, x - 2.4, gy + 1.6 + r * 6, 4.8, 2.6, k === 'shoes' ? ['#5a3a24', '#1b1d24', '#e2493b'][i % 3] : col(i + r * 5));
          rect(c, '#ffffff', x - 2.4, gy + 3.6 + r * 6, 4.8, 0.6);
        }
      }
      return;
    case 'tea':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 1.8, gy + gh - 8, 3.6, 6, 'rgba(255,250,240,0.95)');
        rect(c, ['#c49a6c', '#e8a0b0', '#9ad07a', '#f0c870'][i % 4], x - 1.4, gy + gh - 5.6, 2.8, 3);
        for (let k2 = 0; k2 < 2; k2++) ellipse(c, '#2a1810', x - 0.7 + k2 * 1.4, gy + gh - 3, 0.45, 0.45);
        rect(c, '#e2493b', x + 0.4, gy + gh - 10, 0.5, 3);
      }
      return;
    case 'chicken':
      for (let i = 0; i < n; i++) ellipse(c, '#d8902a', gx + step * (i + 0.5), gy + gh - 5, step * 0.42, 3);
      for (let i = 0; i < n; i++) ellipse(c, '#f0b04a', gx + step * (i + 0.5) - 1, gy + gh - 6, step * 0.2, 1.2);
      return;
    case 'ice':
    case 'fruit':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.5, gy + gh - 4, 5, 2.4, '#ffffff');
        ellipse(c, k === 'ice' ? '#fff8f0' : '#f4e8c0', x, gy + gh - 5, 2.6, 2);
        ellipse(c, ['#ffb030', '#ff6a8a', '#7ad04a', '#c070ff'][i % 4], x, gy + gh - 6.2, 1.8, 1.2);
      }
      if (k === 'fruit') for (let i = 0; i < n * 2; i++) ellipse(c, ['#ff8a2a', '#ffd34e', '#e2493b', '#7ad04a'][i % 4], gx + 2 + i * (gw / (n * 2)), gy + 3, 1.4, 1.4);
      return;
    case 'records':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.6, gy + 2, 5.2, 5.2, col(i));
        ellipse(c, '#1b1d24', x, gy + gh - 4, 2.4, 2.4);
        ellipse(c, col(i + 3), x, gy + gh - 4, 0.8, 0.8);
      }
      return;
    case 'tattoo':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.4, gy + 2, 4.8, 6, '#f4ead0');
        if (i % 2) {
          ellipse(c, '#e2493b', x, gy + 4.4, 1.4, 1.2);
          line(c, '#2a7a3a', 0.5, [x, gy + 5.4, x, gy + 7.4]);
        } else line(c, '#1b5a8a', 0.7, [x - 1.6, gy + 6.6, x - 0.4, gy + 3.2, x + 0.8, gy + 5.6, x + 1.8, gy + 3]);
      }
      return;
    case 'nails':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 1, gy + gh - 7, 2, 4, col(i));
        rect(c, '#1b1d24', x - 0.6, gy + gh - 8.4, 1.2, 1.6);
      }
      ellipse(c, '#f6cfa8', gx + gw / 2, gy + 4, 3, 1.8);
      for (let f = -2; f <= 2; f++) ellipse(c, '#ff4a8a', gx + gw / 2 + f * 1.2, gy + 2.6, 0.5, 0.7);
      return;
    case 'drugstore':
    case 'mart':
      for (let r = 0; r < 3; r++) {
        rect(c, '#c8ccd0', gx + 1, gy + 3 + r * 4, gw - 2, 0.5);
        for (let i = 0; i < n * 2; i++) rect(c, col(i + r * 9), gx + 1.5 + i * (gw - 3) / (n * 2), gy + 0.8 + r * 4, (gw - 3) / (n * 2) - 0.6, 2.2);
      }
      return;
    case 'sparkle':
      for (let i = 0; i < n * 2; i++) {
        const x = gx + 2 + hash(i, seed + 1) * (gw - 4), y = gy + 2 + hash(i, seed + 2) * (gh - 5);
        ellipse(c, col(i), x, y, 0.9, 0.9);
        rect(c, 'rgba(255,255,255,0.8)', x - 0.2, y - 1.4, 0.4, 2.8);
      }
      return;
    case 'phone':
      for (let r = 0; r < 2; r++) for (let i = 0; i < n; i++) block(c, gx + step * (i + 0.5) - 1.4, gy + 1.5 + r * 6, 2.8, 5, col(i + r * 4));
      return;
    case 'noodle':
    case 'ramen':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        ellipse(c, '#f4f1ea', x, gy + gh - 4, 2.6, 1.6);
        ellipse(c, k === 'ramen' ? '#d8a050' : '#8a4a2a', x, gy + gh - 4.6, 2, 0.8);
      }
      if (k === 'ramen') for (let i = 0; i < n; i++) block(c, gx + step * i + 0.5, gy, step - 1, 5, i % 2 ? '#1b1d24' : '#2a2a4a');
      else rect(c, 'rgba(255,255,255,0.35)', gx, gy + 1, gw, 1);
      return;
    case 'sticker':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.2, gy + 1.5, 4.4, gh - 4, '#ffffff');
        rect(c, col(i), x - 1.6, gy + 2.2, 3.2, 2.6);
        ellipse(c, '#f6cfa8', x, gy + 6, 1.2, 1.2);
        ellipse(c, '#ff4a8a', x + 1.2, gy + 4.6, 0.6, 0.6);
      }
      return;
    case 'ktv':
      vgrad(c, gx, gy, gw, gh, '#3a1a6a', '#1a0a3a');
      for (let i = 0; i < 6; i++) ellipse(c, col(i), gx + hash(i, seed) * gw, gy + hash(i + 9, seed) * gh, 0.7, 0.7);
      line(c, '#c8ccd0', 0.8, [gx + gw / 2, gy + gh - 1, gx + gw / 2, gy + 4]);
      ellipse(c, '#2a2a2e', gx + gw / 2, gy + 3.4, 1.5, 1.8);
      return;
    case 'hotel':
      block(c, gx + gw / 2 - 4, gy + 1, 8, gh - 1, '#8ab0c8');
      rect(c, '#5a7a90', gx + gw / 2 - 0.2, gy + 1, 0.4, gh - 1);
      txt(c, 'HOTEL', gx + gw / 2, gy + 3, 2.2, '#1f3a6a', 900);
      return;
    case 'books':
      for (let r = 0; r < 2; r++) {
        rect(c, '#7a5030', gx + 1, gy + 5 + r * 6, gw - 2, 0.8);
        for (let i = 0; i < n * 2; i++) rect(c, col(i + r * 7), gx + 1.5 + i * (gw - 3) / (n * 2), gy + 1 + r * 6, (gw - 3) / (n * 2) - 0.5, 4);
      }
      return;
    case 'glasses':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        for (const dx of [-1.3, 1.3]) {
          c.strokeStyle = ['#1b1d24', '#c0392b', '#8a5a2a'][i % 3];
          c.lineWidth = 0.6;
          c.beginPath();
          c.arc(x + dx, gy + gh / 2, 1.1, 0, Math.PI * 2);
          c.stroke();
        }
      }
      return;
    case 'anime':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.6, gy + 1, 5.2, 7, col(i));
        ellipse(c, '#f8d8bc', x, gy + 3.6, 1.6, 1.6);
        ellipse(c, '#1b1d24', x - 0.6, gy + 3.6, 0.3, 0.5);
        ellipse(c, '#1b1d24', x + 0.6, gy + 3.6, 0.3, 0.5);
        block(c, x - 1, gy + gh - 5, 2, 4, col(i + 2));
      }
      return;
    case 'bank':
      block(c, gx + 2, gy + 1, 6, gh - 2, '#c8ccd0');
      rect(c, '#2a5a8a', gx + 3, gy + 2.4, 4, 3);
      rect(c, '#1b1d24', gx + 3.5, gy + 7, 3, 0.6);
      txt(c, 'ATM', gx + 5, gy + 10, 2, '#1f3a6a', 900);
      return;
    case 'coffee':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 1.6, gy + gh - 6, 3.2, 4.2, '#f4f1ea');
        rect(c, '#5a3a2a', x - 1.6, gy + gh - 4.6, 3.2, 1);
      }
      ellipse(c, '#ffd38a', gx + gw / 2, gy + 2, 1.6, 1.2);
      return;
    case 'tv':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.8, gy + 2, 5.6, 4, '#1b1d24');
        vgrad(c, x - 2.2, gy + 2.6, 4.4, 2.8, col(i), col(i + 1));
        rect(c, '#55606a', x - 0.4, gy + 6, 0.8, 1.4);
      }
      return;
    case 'sports':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        ellipse(c, ['#ff8a2a', '#ffffff', '#ffd34e', '#3a8fd9'][i % 4], x, gy + gh - 3.4, 2, 2);
        line(c, 'rgba(0,0,0,0.3)', 0.4, [x - 2, gy + gh - 3.4, x + 2, gy + gh - 3.4]);
      }
      for (let i = 0; i < n; i++) poly(c, col(i + 4), [gx + step * (i + 0.5) - 2.4, gy + 2, gx + step * (i + 0.5) + 2.4, gy + 2, gx + step * (i + 0.5) + 1.8, gy + 7, gx + step * (i + 0.5) - 1.8, gy + 7]);
      return;
    case 'arcade':
    case 'claw':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        block(c, x - 2.6, gy + 1, 5.2, gh - 1, k === 'claw' ? col(i) : '#2a2a3a');
        rect(c, k === 'claw' ? 'rgba(220,240,255,0.9)' : col(i), x - 1.8, gy + 2, 3.6, 4.6);
        if (k === 'claw') for (let p = 0; p < 3; p++) ellipse(c, col(i + p + 2), x - 1 + p, gy + 6, 0.8, 0.8);
      }
      return;
    case 'herbs':
      for (let i = 0; i < n * 2; i++) {
        const x = gx + 1.5 + i * (gw - 3) / (n * 2);
        ellipse(c, ['#3f7a35', '#5a8a3a', '#7a9a4a', '#4a6a2a'][i % 4], x + 1, gy + gh - 3, 2.2, 2.4);
        rect(c, '#c8b080', x + 0.4, gy + gh - 2, 1.4, 0.6);
      }
      for (let i = 0; i < n; i++) ellipse(c, '#6a8a3a', gx + step * (i + 0.5), gy + 3, 2.2, 1.6);
      return;
    case 'drawers':
      for (let r = 0; r < 3; r++) for (let i = 0; i < n * 2; i++) {
        const x = gx + 1 + i * (gw - 2) / (n * 2), y = gy + 1 + r * 3.6;
        rect(c, '#8a5a30', x, y, (gw - 2) / (n * 2) - 0.4, 3.2);
        rect(c, '#e8c860', x + (gw - 2) / (n * 4) - 0.5, y + 1.4, 0.8, 0.5);
      }
      return;
    case 'gold':
      for (let i = 0; i < n * 2; i++) {
        const x = gx + 1.5 + i * (gw - 3) / (n * 2);
        for (let s = 0; s < 3; s++) rect(c, s % 2 ? '#e8c040' : '#c89a2a', x, gy + gh - 2.4 - s * 1.4, (gw - 3) / (n * 2) - 0.6, 1.2);
        rect(c, '#c0392b', x, gy + gh - 7, (gw - 3) / (n * 2) - 0.6, 2.4);
      }
      for (let i = 0; i < n; i++) rect(c, '#c0392b', gx + step * (i + 0.5) - 0.3, gy + 1, 0.6, 4);
      return;
    case 'teaJars':
      for (let r = 0; r < 2; r++) for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5), y = gy + 1 + r * 6;
        block(c, x - 2, y, 4, 4.6, ['#5a7a3a', '#3a2a1a', '#c0392b', '#2a4a6a'][(i + r) % 4]);
        rect(c, '#e8c860', x - 1.2, y + 1.6, 2.4, 1.4);
      }
      return;
    case 'buddha':
      for (let i = 0; i < n; i++) {
        const x = gx + step * (i + 0.5);
        ellipse(c, '#e8b830', x, gy + gh - 3, 2.4, 2);
        ellipse(c, '#f0c840', x, gy + gh - 6.4, 1.3, 1.4);
        ellipse(c, 'rgba(255,240,180,0.5)', x, gy + gh - 6.4, 2.6, 2.6);
      }
      return;
    case 'lanterns':
      for (let i = 0; i < n; i++) lantern(c, gx + step * (i + 0.5), gy + 4 + (i % 2) * 2, 2.2, false, i % 3 === 1 ? '#f0c040' : '#d8282a');
      return;
    case 'seals':
      for (let i = 0; i < n * 2; i++) {
        const x = gx + 1.5 + i * (gw - 3) / (n * 2);
        block(c, x, gy + gh - 6, 1.8, 4, ['#c8a070', '#8a5a30', '#e8e0d0'][i % 3]);
      }
      rect(c, '#e8473a', gx + 2, gy + 2, 3, 3);
      return;
    default:
      return;
  }
}

/** One movie poster (original films). */
const MOVIES: [string, string, string, string][] = [
  ['太空貓', 'SPACE CAT', '#1b2a5a', '#ffd34e'],
  ['龍少年', 'DRAGON BOY', '#8a1a1a', '#ffe08a'],
  ['鬼公車', 'GHOST BUS', '#1a3a2a', '#9affc0'],
  ['珍奶大冒險', 'BOBA QUEST', '#f0a0b0', '#7a2a10'],
  ['夏日海風', 'SEA BREEZE', '#3aa0d0', '#ffffff'],
  ['機器人阿福', 'ROBO FU', '#5a5a6a', '#4ae0ff'],
  ['颱風來了', 'TYPHOON!', '#2a3a4a', '#ff9a3a'],
  ['夜市之王', 'NIGHT MARKET KING', '#c0392b', '#ffd34e'],
];
function poster(c: Ctx, x: number, y: number, w: number, h: number, i: number): void {
  const [zh, en, bg, fg] = MOVIES[i % MOVIES.length];
  block(c, x, y, w, h, '#1b1d24');
  vgrad(c, x + 0.8, y + 0.8, w - 1.6, h - 1.6, bg, shade(bg, -0.35));
  const cx = x + w / 2;
  switch (i % 8) {
    case 0: // a cat in a helmet among stars
      for (let k = 0; k < 5; k++) rect(c, '#ffffff', x + 1.5 + hash(k, 1) * (w - 3), y + 1.5 + hash(k, 2) * (h * 0.4), 0.5, 0.5);
      ellipse(c, '#ff9f43', cx, y + h * 0.48, w * 0.22, w * 0.2);
      poly(c, '#ff9f43', [cx - w * 0.2, y + h * 0.4, cx - w * 0.12, y + h * 0.3, cx - w * 0.05, y + h * 0.4]);
      poly(c, '#ff9f43', [cx + w * 0.2, y + h * 0.4, cx + w * 0.12, y + h * 0.3, cx + w * 0.05, y + h * 0.4]);
      ellipse(c, 'rgba(200,240,255,0.35)', cx, y + h * 0.46, w * 0.32, w * 0.3);
      break;
    case 1: // a dragon curling up
      c.strokeStyle = '#ffd34e';
      c.lineWidth = w * 0.12;
      c.beginPath();
      c.moveTo(x + w * 0.2, y + h * 0.75);
      c.quadraticCurveTo(x + w * 0.9, y + h * 0.55, x + w * 0.4, y + h * 0.4);
      c.quadraticCurveTo(x + w * 0.05, y + h * 0.25, x + w * 0.7, y + h * 0.18);
      c.stroke();
      ellipse(c, '#ff5a3a', x + w * 0.72, y + h * 0.17, w * 0.1, w * 0.08);
      break;
    case 2: // a ghostly bus with glowing windows
      block(c, x + w * 0.12, y + h * 0.35, w * 0.76, h * 0.25, '#3a5a4a');
      for (let k = 0; k < 3; k++) rect(c, '#9affc0', x + w * 0.18 + k * w * 0.22, y + h * 0.4, w * 0.14, h * 0.08);
      ellipse(c, 'rgba(160,255,200,0.3)', cx, y + h * 0.48, w * 0.5, h * 0.2);
      break;
    case 3: // a giant bubble tea
      block(c, cx - w * 0.2, y + h * 0.25, w * 0.4, h * 0.42, 'rgba(255,250,240,0.95)');
      rect(c, '#c49a6c', cx - w * 0.17, y + h * 0.4, w * 0.34, h * 0.25);
      for (let k = 0; k < 4; k++) ellipse(c, '#2a1810', cx - w * 0.12 + k * w * 0.08, y + h * 0.6, 0.7, 0.7);
      rect(c, '#e2493b', cx + w * 0.05, y + h * 0.12, 0.8, h * 0.18);
      break;
    case 4: // sun over waves
      ellipse(c, '#ffd34e', cx, y + h * 0.35, w * 0.2, w * 0.2);
      for (let k = 0; k < 3; k++) rect(c, 'rgba(255,255,255,0.7)', x + 1.5, y + h * (0.55 + k * 0.07), w - 3, 0.6);
      break;
    case 5: // a friendly robot
      block(c, cx - w * 0.18, y + h * 0.28, w * 0.36, h * 0.2, '#c8ccd0');
      rect(c, '#4ae0ff', cx - w * 0.1, y + h * 0.34, w * 0.06, w * 0.06);
      rect(c, '#4ae0ff', cx + w * 0.05, y + h * 0.34, w * 0.06, w * 0.06);
      block(c, cx - w * 0.22, y + h * 0.5, w * 0.44, h * 0.18, '#9aa0a8');
      break;
    case 6: // typhoon spiral
      c.strokeStyle = 'rgba(255,255,255,0.8)';
      c.lineWidth = 0.8;
      c.beginPath();
      for (let a = 0; a < 12; a += 0.4) c.lineTo(cx + Math.cos(a) * a * w * 0.03, y + h * 0.42 + Math.sin(a) * a * w * 0.03);
      c.stroke();
      break;
    default: // a lantern-lit stall
      lantern(c, cx - w * 0.2, y + h * 0.3, w * 0.09, false);
      lantern(c, cx + w * 0.2, y + h * 0.3, w * 0.09, false);
      block(c, cx - w * 0.3, y + h * 0.48, w * 0.6, h * 0.15, '#9a6a44');
      break;
  }
  fit(c, zh, cx, y + h * 0.78, Math.min(5.4, w * 0.24), fg, w - 2, 'rgba(0,0,0,0.5)');
  fit(c, en, cx, y + h * 0.9, Math.min(2.4, w * 0.12), '#ffffff', w - 2);
}

/** Taiwanese iron window grille (鐵窗) over a window. */
function grille(c: Ctx, x: number, y: number, w: number, h: number): void {
  rect(c, 'rgba(60,64,70,0.75)', x - 0.6, y - 0.6, w + 1.2, 0.6);
  rect(c, 'rgba(60,64,70,0.75)', x - 0.6, y + h, w + 1.2, 0.6);
  for (let i = x + 1.2; i < x + w; i += 2.4) rect(c, 'rgba(60,64,70,0.55)', i, y, 0.35, h);
  line(c, 'rgba(60,64,70,0.55)', 0.35, [x, y + h * 0.5, x + w, y + h * 0.5]);
}

function upperWindows(c: Ctx, x0: number, w: number, top: number, bottom: number, wall: string, night: boolean, seed: number, old: boolean): void {
  const rows = Math.max(1, Math.floor((bottom - top) / 13));
  const cols = Math.max(1, Math.floor(w / 15));
  const cw = w / cols;
  for (let r = 0; r < rows; r++) {
    const y = top + 3 + r * 13;
    for (let i = 0; i < cols; i++) {
      const x = x0 + i * cw + 2.5, ww = cw - 5;
      const lit = night && hash(i + r * 7, seed) < 0.6;
      block(c, x, y, ww, 8, shade(wall, -0.35));
      if (lit) vgrad(c, x + 0.8, y + 0.8, ww - 1.6, 6.4, '#fff0c0', '#ffc870');
      else vgrad(c, x + 0.8, y + 0.8, ww - 1.6, 6.4, '#8ab8d8', '#4a7aa0');
      rect(c, lit ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.35)', x + 1.2, y + 1.2, ww * 0.3, 0.5);
      if (hash(i, r + seed) < 0.55) grille(c, x - 0.6, y - 0.4, ww + 1.2, 8.8);
      // Air conditioner box under some windows, with its drip pipe.
      if (hash(i + 3, r + seed) < 0.4) {
        block(c, x + ww - 7, y + 8.6, 6.4, 3.4, '#e8e6e0');
        rect(c, '#9aa0a6', x + ww - 6, y + 9.6, 4.4, 0.4);
        rect(c, '#9aa0a6', x + ww - 6, y + 10.6, 4.4, 0.4);
      }
      if (old && hash(i + 9, r + seed) < 0.5) {
        // Laundry on a pole and a potted plant on the sill.
        line(c, '#8a8f96', 0.4, [x - 1, y + 9, x + ww + 1, y + 9]);
        rect(c, ['#ff6aa0', '#3a8fd9', '#f4f1ea'][i % 3], x + 1, y + 9, 2, 2.6);
        ellipse(c, '#3f7a35', x + ww - 1.5, y + 7.6, 1.6, 1.2);
      }
    }
  }
}

function modernFacade(c: Ctx, s: ShopSpec, x0: number, base: number, w: number, h: number, night: boolean, seed: number): void {
  const top = base - h;
  // Wall with tile texture.
  block(c, x0, top, w, h, s.wall);
  for (let i = x0 + 4; i < x0 + w - 1; i += 4) rect(c, 'rgba(0,0,0,0.05)', i, top + 1, 0.4, h - 2);
  for (let j = top + 4; j < base - 20; j += 4) rect(c, 'rgba(0,0,0,0.04)', x0 + 1, j, w - 2, 0.4);
  vgrad(c, x0 + 1, top + h * 0.5, w - 2, h * 0.3, 'rgba(0,0,0,0)', 'rgba(60,40,30,0.1)');
  rect(c, shade(s.wall, 0.15), x0 + 0.6, top + 0.6, w - 1.2, 1.6);
  upperWindows(c, x0, w, top + 2, base - 32, s.wall, night, seed, false);
  // Lightbox sign band.
  const by = base - 31;
  block(c, x0 + 1, by, w - 2, 12, s.band, shade(s.band, 0.25), shade(s.band, -0.3));
  if (night) rect(c, 'rgba(255,255,255,0.12)', x0 + 2, by + 1, w - 4, 10);
  fit(c, s.zh, x0 + w / 2, by + 5, 7.4, night ? shade(s.ink, 0.2) : s.ink, w - 8, 'rgba(0,0,0,0.4)');
  fit(c, s.en, x0 + w / 2, by + 10, 2.2, night ? '#ffffff' : 'rgba(255,255,255,0.85)', w - 6);
  // Shopfront: glass, goods, an open door.
  const gy = base - 18;
  block(c, x0 + 1.5, gy, w - 3, 18, '#3a3e44', '#55606a');
  const gw = w - 6;
  if (night) vgrad(c, x0 + 3, gy + 1.5, gw, 15.5, '#fff6dc', '#ffe0a8');
  else vgrad(c, x0 + 3, gy + 1.5, gw, 15.5, '#cfe6f2', '#a8c8da');
  goods(c, s.goods, x0 + 3, gy + 2, gw, 14.5, seed);
  rect(c, 'rgba(255,255,255,0.35)', x0 + 4, gy + 2, 0.6, 12);
  rect(c, 'rgba(255,255,255,0.18)', x0 + 6, gy + 2, 2, 12);
  const dx = x0 + w / 2 - 4;
  block(c, dx, gy + 3, 8, 15, night ? '#ffe8b8' : '#6a7a88');
  rect(c, '#2a2e33', dx + 3.8, gy + 3, 0.4, 15);
  // Little awning lip and a step.
  rect(c, shade(s.band, -0.2), x0 + 1, gy - 1, w - 2, 1.4);
  rect(c, '#9a968e', x0, base - 1.4, w, 1.4);
  if (night) {
    ellipse(c, 'rgba(255,240,200,0.18)', x0 + w / 2, base + 2, w * 0.5, 5);
    ellipse(c, `rgba(255,255,255,0.08)`, x0 + w / 2, by + 6, w * 0.6, 10);
  }
}

function cinemaFacade(c: Ctx, s: ShopSpec, x0: number, base: number, w: number, h: number, night: boolean): void {
  const top = base - h;
  block(c, x0, top, w, h, s.wall);
  // Art-deco fins and a stepped crown.
  rect(c, shade(s.wall, 0.2), x0 + 1, top + 1, w - 2, 2);
  for (let i = x0 + 6; i < x0 + w - 4; i += 12) vgrad(c, i, top + 3, 3, h - 34, shade(s.wall, 0.3), shade(s.wall, -0.1));
  // Marquee: the big name board with bulbs around it.
  const my = top + 6;
  block(c, x0 + 4, my, w - 8, 16, s.band, shade(s.band, 0.2), shade(s.band, -0.4));
  fit(c, s.zh, x0 + w / 2, my + 6.4, 9, night ? '#fff6c0' : s.ink, w - 16, 'rgba(0,0,0,0.6)');
  fit(c, s.en, x0 + w / 2, my + 12.6, 3, '#ffffff', w - 16);
  // Posters: three now-showing films.
  const py = my + 19, ph = 20, pw = Math.min(14, (w - 16) / 3 - 3);
  const p0 = s.poster ?? 0;
  for (let i = 0; i < 3; i++) {
    const px = x0 + w / 2 + (i - 1) * (pw + 4) - pw / 2;
    block(c, px - 1.4, py - 1.4, pw + 2.8, ph + 2.8, '#e8c860', '#fff0a0');
    poster(c, px, py, pw, ph, p0 + i);
  }
  // Entrance canopy with the showtimes and glass doors.
  const cy = base - 18;
  block(c, x0 + 2, cy - 4, w - 4, 4, '#2a2e33', '#55606a');
  for (let i = x0 + 4; i < x0 + w - 4; i += 3) ellipse(c, night ? '#fff3b0' : '#e8e0c0', i, cy - 2, 0.6, 0.6);
  block(c, x0 + 3, cy, w - 6, 18, '#1b1d24');
  for (let i = 0; i < 4; i++) {
    const dx = x0 + 6 + i * ((w - 12) / 4);
    vgrad(c, dx, cy + 2, (w - 12) / 4 - 2, 16, night ? '#fff0c0' : '#8ab0c8', night ? '#ffc870' : '#4a6a80');
    rect(c, '#c8ccd0', dx + (w - 12) / 8 - 1.5, cy + 9, 2, 0.6);
  }
  rect(c, '#9a968e', x0, base - 1.4, w, 1.4);
  if (night) ellipse(c, 'rgba(255,220,140,0.2)', x0 + w / 2, base, w * 0.55, 8);
}

function towerFacade(c: Ctx, s: ShopSpec, x0: number, base: number, w: number, h: number, night: boolean, seed: number): void {
  const top = base - h;
  block(c, x0, top, w, h, s.wall);
  // Glass curtain wall in a grid.
  for (let j = top + 3; j < base - 22; j += 6) for (let i = x0 + 2; i < x0 + w - 3; i += 6) {
    const lit = night && hash(i, j + seed) < 0.55;
    rect(c, lit ? '#ffe0a0' : hash(i, j) < 0.3 ? '#9ac8e0' : '#5a8aa8', i, j, 5, 5);
  }
  block(c, x0 + 1, base - 22, w - 2, 7, s.band);
  fit(c, s.zh, x0 + w / 2, base - 18.6, 5.4, s.ink, w - 6);
  const gy = base - 15;
  block(c, x0 + 1.5, gy, w - 3, 15, '#3a3e44');
  vgrad(c, x0 + 3, gy + 1.5, w - 6, 12.5, night ? '#fff6dc' : '#cfe6f2', night ? '#ffe0a8' : '#a8c8da');
  goods(c, 'drugstore', x0 + 3, gy + 2, w / 2 - 6, 11, seed);
  goods(c, 'clothes', x0 + w / 2 + 3, gy + 2, w / 2 - 6, 11, seed + 1);
  rect(c, '#9a968e', x0, base - 1.4, w, 1.4);
}

/** Old Wanhua shophouse: two storeys, pastel tiles, a balcony, a hand-painted signboard. */
function oldFacade(c: Ctx, s: ShopSpec, x0: number, base: number, w: number, h: number, night: boolean, seed: number): void {
  const top = base - h;
  block(c, x0, top, w, h, s.wall);
  // Small square wall tiles and the grime of decades.
  for (let j = top + 2; j < base - 22; j += 3) for (let i = x0 + 1; i < x0 + w - 1; i += 3) if (hash(i, j + seed) < 0.3) rect(c, 'rgba(255,255,255,0.12)', i, j, 2.4, 2.4);
  vgrad(c, x0 + 1, top + 1, w - 2, 8, 'rgba(40,30,20,0.25)', 'rgba(40,30,20,0)');
  // Parapet with a little name plaque.
  block(c, x0 + 1, top - 2, w - 2, 4, shade(s.wall, -0.15));
  upperWindows(c, x0, w, top + 3, base - 30, s.wall, night, seed, true);
  // Balcony rail.
  rect(c, '#55606a', x0 + 1, base - 27, w - 2, 0.8);
  for (let i = x0 + 2; i < x0 + w - 1; i += 2) rect(c, '#55606a', i, base - 27, 0.35, 3);
  // Signboard: black-lacquered board with gold characters, or a painted one.
  const by = base - 24;
  block(c, x0 + 2, by, w - 4, 9, s.band, shade(s.band, 0.2), shade(s.band, -0.3));
  rect(c, '#e8c860', x0 + 3, by + 0.8, w - 6, 0.4);
  fit(c, s.zh, x0 + w / 2, by + 4.8, 6.4, s.ink, w - 10, 'rgba(0,0,0,0.4)');
  // Open shopfront under a half-rolled shutter.
  const gy = base - 15;
  block(c, x0 + 1.5, gy, w - 3, 15, '#4a3a2a');
  vgrad(c, x0 + 3, gy + 3.5, w - 6, 11, night ? '#ffe8b0' : '#6a5a48', night ? '#ffc870' : '#3a2e24');
  for (let j = gy + 0.6; j < gy + 3.5; j += 0.8) rect(c, '#9aa0a6', x0 + 2.4, j, w - 4.8, 0.5);
  goods(c, s.goods, x0 + 3, gy + 3.5, w - 6, 11, seed);
  rect(c, '#8a8278', x0, base - 1.4, w, 1.4);
  if (night) ellipse(c, 'rgba(255,200,120,0.2)', x0 + w / 2, base, w * 0.5, 6);
}

/** Bopiliao: Qing and Japanese-era red-brick arcades (騎樓), each building a little different. */
function arcadeFacade(c: Ctx, s: ShopSpec, x0: number, base: number, w: number, h: number, night: boolean, seed: number): void {
  const top = base - h + 8;
  const v = s.poster ?? 0;
  block(c, x0, top, w, h - 8, s.wall);
  // Brick courses.
  for (let j = top + 2; j < base - 2; j += 2.4) {
    const off = Math.floor((j - top) / 2.4) % 2 ? 2.5 : 0;
    for (let i = x0 + 1 + off; i < x0 + w - 1; i += 5) rect(c, 'rgba(0,0,0,0.13)', i, j, 0.4, 2.4);
    rect(c, 'rgba(255,220,190,0.12)', x0 + 1, j, w - 2, 0.4);
  }
  // Crown: a stepped Baroque pediment (Japanese era) or a tiled eave (Qing).
  if (v % 2) {
    poly(c, '#e8dcc4', [x0 + 3, top + 1, x0 + w / 2 - 8, top + 1, x0 + w / 2 - 5, top - 6, x0 + w / 2, top - 9, x0 + w / 2 + 5, top - 6, x0 + w / 2 + 8, top + 1, x0 + w - 3, top + 1, x0 + w - 3, top + 3, x0 + 3, top + 3]);
    ellipse(c, '#c8b898', x0 + w / 2, top - 3, 2.4, 2.4);
    ellipse(c, '#e8dcc4', x0 + w / 2, top - 3, 1.4, 1.4);
  } else {
    block(c, x0, top - 5, w, 5, '#4a4e54', '#6a7078');
    for (let i = x0 + 1; i < x0 + w - 1; i += 2.4) rect(c, 'rgba(0,0,0,0.25)', i, top - 4.6, 0.4, 4.2);
    poly(c, '#4a4e54', [x0 - 1, top - 2, x0 + 3, top - 5, x0 + 2, top - 1]);
    poly(c, '#4a4e54', [x0 + w + 1, top - 2, x0 + w - 3, top - 5, x0 + w - 2, top - 1]);
  }
  rect(c, '#e8dcc4', x0 + 1, top + 3, w - 2, 1.2);
  // Upper floor: arched windows with white keystones and green shutters.
  const nw = Math.max(2, Math.floor(w / 26));
  for (let i = 0; i < nw; i++) {
    const cx = x0 + (i + 0.5) * (w / nw), wy = top + 8;
    ellipse(c, '#e8dcc4', cx, wy + 2, 6.4, 4.4);
    block(c, cx - 5, wy + 2, 10, 13, '#e8dcc4');
    ellipse(c, night ? '#ffd890' : '#3a4a52', cx, wy + 2.6, 4.6, 3.6);
    rect(c, night ? '#ffd890' : '#3a4a52', cx - 4.6, wy + 2.6, 9.2, 11);
    rect(c, '#2f6a5a', cx - 7.4, wy + 3, 2.4, 11);
    rect(c, '#2f6a5a', cx + 5, wy + 3, 2.4, 11);
    rect(c, '#e8dcc4', cx - 0.4, wy + 2.6, 0.8, 11);
    rect(c, '#c8b898', cx - 1, wy - 2.4, 2, 2.6);
  }
  // Name board between the floors.
  const by = base - 36;
  block(c, x0 + w / 2 - Math.min(w / 2 - 3, 22), by, Math.min(w - 6, 44), 8, s.band, shade(s.band, 0.2));
  fit(c, s.zh, x0 + w / 2, by + 4.3, 5.4, s.ink, Math.min(w - 10, 40));
  rect(c, '#e8dcc4', x0 + 1, base - 27, w - 2, 1.6);
  // The arcade: arches on brick piers with the shaded walkway behind.
  const na = Math.max(2, Math.floor(w / 22));
  const aw = w / na;
  for (let i = 0; i < na; i++) {
    const ax = x0 + i * aw;
    vgrad(c, ax + 3, base - 24, aw - 6, 23, night ? '#5a4030' : '#2a1e18', night ? '#8a6040' : '#4a3628');
    ellipse(c, s.wall, ax + aw / 2, base - 24, (aw - 6) / 2, 3);
    c.fillStyle = night ? '#5a4030' : '#2a1e18';
    c.beginPath();
    c.ellipse(ax + aw / 2, base - 22, (aw - 6) / 2, 4, 0, Math.PI, 0);
    c.fill();
    if (s.goods !== 'none') goods(c, s.goods, ax + 5, base - 15, aw - 10, 12, seed + i);
    else {
      // Old photos and history panels.
      block(c, ax + aw / 2 - 5, base - 17, 10, 8, '#e8dcc4');
      rect(c, '#8a7a60', ax + aw / 2 - 4, base - 16, 8, 6);
      rect(c, '#c8b898', ax + aw / 2 - 3, base - 15, 3, 4);
    }
    if (night) ellipse(c, 'rgba(255,200,120,0.3)', ax + aw / 2, base - 20, 3, 2);
    rect(c, shade(s.wall, -0.2), ax, base - 24, 3, 24);
  }
  rect(c, shade(s.wall, -0.2), x0 + w - 3, base - 24, 3, 24);
  rect(c, '#8a8278', x0, base - 1.4, w, 1.4);
}

function shopFacade(c: Ctx, d: Decor): void {
  const k = XM_SHOPS[d.variant ?? 0];
  const s = SHOPS[k];
  const w = d.w ?? 64, h = d.h ?? 64;
  const x0 = d.x - w / 2, base = d.y;
  const night = isNight();
  const style = s.style ?? 'modern';
  const extra = style === 'arcade' ? 12 : 4;
  cachedArt(c, `shop:${k}:${w}:${h}:${night ? 1 : 0}`, x0 - 2, base - h - extra, w + 4, h + extra + 2, (cc) => {
    const seed = (d.variant ?? 0) * 13 + w;
    if (style === 'cinema') cinemaFacade(cc, s, x0, base, w, h, night);
    else if (style === 'tower') towerFacade(cc, s, x0, base, w, h, night, seed);
    else if (style === 'old') oldFacade(cc, s, x0, base, w, h, night, seed);
    else if (style === 'arcade') arcadeFacade(cc, s, x0, base, w, h, night, seed);
    else modernFacade(cc, s, x0, base, w, h, night, seed);
  });
}

/** Chasing marquee bulbs on the cinemas (drawn live on top of the cached facade). */
function marquee(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 64, h = d.h ?? 64;
  const x0 = d.x - w / 2 + 4, y0 = d.y - h + 6, mw = w - 8, mh = 16;
  const night = isNight();
  const k = Math.floor(t * 6);
  const per = Math.floor((mw + mh) * 2 / 4);
  for (let i = 0; i < per; i++) {
    const p = (i * 4) % ((mw + mh) * 2);
    let x: number, y: number;
    if (p < mw) (x = x0 + p), (y = y0);
    else if (p < mw + mh) (x = x0 + mw), (y = y0 + p - mw);
    else if (p < mw * 2 + mh) (x = x0 + mw - (p - mw - mh)), (y = y0 + mh);
    else (x = x0), (y = y0 + mh - (p - mw * 2 - mh));
    const on = (i + k) % 3 === 0;
    ellipse(c, on ? '#fff6b0' : night ? '#b08a3a' : '#c8b070', x, y, 0.7, 0.7);
    if (on && night) ellipse(c, 'rgba(255,230,140,0.35)', x, y, 2, 2);
  }
}

// ---------------------------------------------------------------------------
// Vertical neon signs and giant screens
// ---------------------------------------------------------------------------

function vsign(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const [text, bg, fg] = XM_VSIGNS[v % XM_VSIGNS.length];
  const chars = [...text];
  const x = d.x, top = d.y - 90, bot = d.y - 30;
  const night = isNight();
  cachedArt(c, `vs:${v}:${night ? 1 : 0}`, x - 10, top - 4, 20, bot - top + 10, (cc) => {
    rect(cc, '#3a3e44', x - 6, top + 6, 12, 1.2);
    rect(cc, '#3a3e44', x - 6, bot - 8, 12, 1.2);
    if (night) ellipse(cc, `${fg}33`, x, (top + bot) / 2, 9, (bot - top) / 2 + 4);
    block(cc, x - 5, top, 10, bot - top, bg, shade(bg, 0.25), shade(bg, -0.3));
    rect(cc, night ? fg : shade(fg, -0.15), x - 4.2, top + 1.2, 8.4, 0.5);
    rect(cc, night ? fg : shade(fg, -0.15), x - 4.2, bot - 1.8, 8.4, 0.5);
    const step = (bot - top - 6) / chars.length;
    const size = Math.min(7, step * 0.92);
    chars.forEach((ch, i) => {
      const cy = top + 4 + step * (i + 0.5);
      if (night) txt(cc, ch, x, cy, size + 0.6, `${fg}66`, 900);
      txt(cc, ch, x, cy, size, night ? '#ffffff' : fg, 900, night ? undefined : 'rgba(0,0,0,0.4)');
    });
  });
  // A few signs flicker at night.
  if (night && hash(v, 7) < 0.35 && Math.sin(t * 9 + v * 3) > 0.93) rect(c, 'rgba(0,0,0,0.45)', x - 4.5, top + 2, 9, (bot - top) / chars.length);
}

/** A rooftop/facade video screen cycling through original adverts. */
function screen(c: Ctx, d: Decor, t: number): void {
  const w = (d.w ?? 96) - 6;
  const sh = Math.round(w * 0.5);
  const x0 = d.x - w / 2, y0 = d.y - 92;
  const night = isNight();
  // Steel frame and legs.
  rect(c, '#3a3e44', x0 + 6, y0 + sh, 2, 92 - sh - 60);
  rect(c, '#3a3e44', x0 + w - 8, y0 + sh, 2, 92 - sh - 60);
  block(c, x0 - 2, y0 - 2, w + 4, sh + 4, '#2a2e33', '#4a4e54');
  const sx = x0, sy = y0, sw = w;
  c.save();
  c.beginPath();
  c.rect(sx, sy, sw, sh);
  c.clip();
  const scene = (Math.floor(t / 5) + (d.variant ?? 0)) % 4;
  const k = (t % 5) / 5;
  if (scene === 0) {
    // Concert: beams sweeping over a singer.
    vgrad(c, sx, sy, sw, sh, '#2a0a4a', '#6a1a7a');
    for (let i = 0; i < 3; i++) {
      const a = Math.sin(t * 1.5 + i * 2) * 0.6;
      c.fillStyle = ['rgba(255,90,200,0.35)', 'rgba(90,220,255,0.35)', 'rgba(255,230,120,0.35)'][i];
      c.beginPath();
      c.moveTo(sx + sw * (0.2 + i * 0.3), sy + sh);
      c.lineTo(sx + sw * 0.5 + Math.sin(a) * sw * 0.5 - 6, sy);
      c.lineTo(sx + sw * 0.5 + Math.sin(a) * sw * 0.5 + 6, sy);
      c.fill();
    }
    ellipse(c, '#1b0a2a', sx + sw * 0.5, sy + sh * 0.55, 3, 3);
    poly(c, '#1b0a2a', [sx + sw * 0.5 - 5, sy + sh, sx + sw * 0.5 - 3, sy + sh * 0.65, sx + sw * 0.5 + 3, sy + sh * 0.65, sx + sw * 0.5 + 5, sy + sh]);
    fit(c, '演唱會 LIVE', sx + sw / 2, sy + sh * 0.2, sh * 0.2, '#ffffff', sw - 4, 'rgba(255,0,150,0.8)');
  } else if (scene === 1) {
    // Bubble tea: a cup with pearls bouncing.
    vgrad(c, sx, sy, sw, sh, '#ffe0b0', '#ffb08a');
    const cx = sx + sw * 0.3, by = sy + sh - 3;
    block(c, cx - 5, by - sh * 0.7, 10, sh * 0.7, 'rgba(255,250,240,0.95)');
    rect(c, '#c49a6c', cx - 4, by - sh * 0.5, 8, sh * 0.5 - 1);
    for (let i = 0; i < 5; i++) ellipse(c, '#2a1810', cx - 3 + i * 1.5, by - 2 - Math.abs(Math.sin(t * 4 + i)) * 2, 0.8, 0.8);
    rect(c, '#e2493b', cx + 1, by - sh * 0.85, 1.2, sh * 0.25);
    fit(c, '珍珠奶茶', sx + sw * 0.68, sy + sh * 0.38, sh * 0.2, '#7a2a10', sw * 0.55);
    fit(c, 'BUBBLE TEA NT$50', sx + sw * 0.68, sy + sh * 0.68, sh * 0.11, '#c0392b', sw * 0.55);
  } else if (scene === 2) {
    // Game trailer: a cat hero flying past clouds.
    vgrad(c, sx, sy, sw, sh, '#3aa0e0', '#9ad8ff');
    for (let i = 0; i < 3; i++) ellipse(c, 'rgba(255,255,255,0.9)', sx + ((i * 40 - t * 20) % (sw + 30) + sw + 30) % (sw + 30) - 15, sy + 4 + i * (sh / 3), 8, 2.6);
    const hx = sx + sw * (0.15 + k * 0.7), hy = sy + sh * 0.55 + Math.sin(t * 5) * 2;
    ellipse(c, '#ff9f43', hx, hy, 4, 3.4);
    poly(c, '#ff9f43', [hx - 3.4, hy - 1.6, hx - 2.2, hy - 4.6, hx - 0.8, hy - 2.4]);
    poly(c, '#ff9f43', [hx + 3.4, hy - 1.6, hx + 2.2, hy - 4.6, hx + 0.8, hy - 2.4]);
    rect(c, '#e2493b', hx - 6, hy + 1, 4, 1.4);
    fit(c, '新遊戲 NEW GAME', sx + sw / 2, sy + sh * 0.18, sh * 0.16, '#ffffff', sw - 4, 'rgba(0,0,80,0.6)');
  } else {
    // Welcome ticker.
    vgrad(c, sx, sy, sw, sh, '#c0306a', '#ff6a8a');
    fit(c, '西門町', sx + sw / 2, sy + sh * 0.38, sh * 0.34, '#ffffff', sw - 6, 'rgba(0,0,0,0.3)');
    c.font = `900 ${sh * 0.14}px ${CJK}`;
    c.textAlign = 'left';
    c.textBaseline = 'middle';
    c.fillStyle = '#fff3b0';
    const msg = 'WELCOME TO XIMENDING ★ 歡迎光臨 ★ ';
    const mw = c.measureText(msg).width;
    const off = (t * 22) % mw;
    c.fillText(msg + msg, sx - off, sy + sh * 0.8);
  }
  // Scanlines and the glass sheen.
  for (let j = sy; j < sy + sh; j += 1.5) rect(c, 'rgba(0,0,0,0.08)', sx, j, sw, 0.5);
  rect(c, 'rgba(255,255,255,0.15)', sx, sy, sw, 1);
  c.restore();
  if (night) ellipse(c, 'rgba(160,200,255,0.12)', d.x, y0 + sh / 2, w * 0.7, sh);
}

// ---------------------------------------------------------------------------
// Ximending street pieces
// ---------------------------------------------------------------------------

/** Cinema Park's graffiti wall: original tags, cartoon faces and a film strip. */
function graffiti(c: Ctx, d: Decor): void {
  const w = d.w ?? 320, x0 = d.x - w / 2, base = d.y, top = base - 40;
  cachedArt(c, `graffiti:${w}`, x0 - 2, top - 2, w + 4, 44, (cc) => {
    block(cc, x0, top, w, 40, '#9a948a', '#b0aaa0');
    for (let j = top + 3; j < base - 1; j += 4) rect(cc, 'rgba(0,0,0,0.06)', x0 + 1, j, w - 2, 0.5);
    const pieces: [string, string, string][] = [['西門', '#ff3a8a', '#ffe14a'], ['TAIPEI', '#2ad0ff', '#1b1d24'], ['PEACE', '#ffd23a', '#7a2aff'], ['電影', '#5aff8a', '#1b1d24'], ['FUN!', '#ff8a2a', '#ffffff'], ['夢', '#c070ff', '#ffffff']];
    const n = pieces.length;
    for (let i = 0; i < n; i++) {
      const cx = x0 + (i + 0.5) * (w / n);
      const [word, fill, edge] = pieces[i];
      // Spray blob behind each piece.
      ellipse(cc, shade(fill, -0.45), cx, base - 20, w / n / 2 - 2, 14);
      ellipse(cc, shade(fill, -0.25), cx - 3, base - 22, w / n / 2 - 6, 10);
      txt(cc, word, cx + 0.8, base - 19.2, word.length > 2 ? 9 : 14, edge, 900);
      txt(cc, word, cx, base - 20, word.length > 2 ? 9 : 14, fill, 900);
      // Drips.
      for (let k = 0; k < 3; k++) rect(cc, fill, cx - 8 + k * 7 + hash(i, k) * 3, base - 13, 0.8, 3 + hash(k, i) * 5);
    }
    // A smiling cartoon face and a film strip running along the top.
    ellipse(cc, '#ffe14a', x0 + w * 0.41, base - 30, 5, 5);
    rect(cc, '#1b1d24', x0 + w * 0.41 - 2, base - 31.4, 1, 1.4);
    rect(cc, '#1b1d24', x0 + w * 0.41 + 1, base - 31.4, 1, 1.4);
    line(cc, '#1b1d24', 0.6, [x0 + w * 0.41 - 2.4, base - 28.6, x0 + w * 0.41, base - 27.4, x0 + w * 0.41 + 2.4, base - 28.6]);
    block(cc, x0 + 4, top + 2, w - 8, 6, '#1b1d24');
    for (let i = x0 + 6; i < x0 + w - 6; i += 4) rect(cc, '#f4f1ea', i, top + 3, 2, 1);
    for (let i = x0 + 6; i < x0 + w - 6; i += 4) rect(cc, '#f4f1ea', i, top + 6, 2, 1);
    txt(cc, '台北電影主題公園 TAIPEI CINEMA PARK', x0 + w / 2, top + 4.6, 3.2, '#ffd34e', 900);
  });
}

function ramp(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 48;
  shadow(c, x, y, w / 2 + 2, 3);
  poly(c, '#7a8088', [x - w / 2, y, x - w / 2 + 8, y - 12, x + w / 2 - 8, y - 12, x + w / 2, y]);
  poly(c, '#9aa0a8', [x - w / 2 + 2, y - 1, x - w / 2 + 8.6, y - 11, x + w / 2 - 8.6, y - 11, x + w / 2 - 2, y - 1]);
  rect(c, '#c8ccd0', x - w / 2 + 8, y - 12.4, w - 16, 1);
  txt(c, 'SKATE', x, y - 6, 4, '#ff3a8a', 900);
}

function reel(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 10, 2.4);
  rect(c, '#55606a', x - 1, y - 14, 2, 14);
  const a = t * 0.6;
  ellipse(c, '#2a2e33', x, y - 22, 9, 9);
  ellipse(c, '#4a4e54', x, y - 22, 8, 8);
  for (let i = 0; i < 5; i++) ellipse(c, '#9aa0a8', x + Math.cos(a + i * 1.256) * 4.6, y - 22 + Math.sin(a + i * 1.256) * 4.6, 1.8, 1.8);
  ellipse(c, '#c8ccd0', x, y - 22, 1.4, 1.4);
}

function bench(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x + 8, y, 15, 2.6);
  rect(c, '#3a3e44', x - 4, y - 7, 1.6, 7);
  rect(c, '#3a3e44', x + 18.4, y - 7, 1.6, 7);
  block(c, x - 6, y - 9, 28, 3.4, '#a8784a', '#c8986a');
  block(c, x - 6, y - 14, 28, 3, '#9a6a40', '#b8885a');
}

const ABOARDS: [string, string, string][] = [['SALE 50%', '#e2493b', '#ffffff'], ['新片上映', '#1b1d24', '#ffd34e'], ['買一送一', '#ff6ab0', '#ffffff']];
function aboard(c: Ctx, d: Decor): void {
  const [t1, bg, fg] = ABOARDS[(d.variant ?? 0) % ABOARDS.length];
  const x = d.x, y = d.y;
  shadow(c, x, y, 6, 1.8);
  poly(c, '#55606a', [x - 5, y, x - 3.6, y - 15, x + 3.6, y - 15, x + 5, y]);
  block(c, x - 4, y - 14, 8, 11, bg);
  fit(c, t1, x, y - 8.6, 3, fg, 7);
}

function balloons(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 7, 2);
  block(c, x - 6, y - 8, 12, 7, '#3a8fd9', '#5aa8e8');
  rect(c, '#1b1d24', x - 5, y - 1.4, 2, 1.4);
  rect(c, '#1b1d24', x + 3, y - 1.4, 2, 1.4);
  const cols = ['#ff4a6a', '#ffd34e', '#57c278', '#9b5fc0', '#4ab0ff', '#ff9f43'];
  for (let i = 0; i < 6; i++) {
    const bx = x - 7 + i * 2.8 + Math.sin(t * 1.3 + i) * 1.2, by = y - 30 - (i % 3) * 4 + Math.sin(t * 1.7 + i * 2) * 1;
    line(c, 'rgba(60,60,60,0.6)', 0.3, [x, y - 8, bx, by + 3]);
    ellipse(c, cols[i], bx, by, 2.6, 3);
    ellipse(c, 'rgba(255,255,255,0.5)', bx - 0.8, by - 1, 0.6, 0.9);
  }
}

const STREETS: [string, string][] = [['漢中街', 'Hanzhong St.'], ['峨眉街', 'Emei St.'], ['武昌街 電影街', 'Cinema St.']];
function streetSign(c: Ctx, d: Decor): void {
  const [zh, en] = STREETS[(d.variant ?? 0) % STREETS.length];
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2, 0.3, 30);
  rect(c, '#55606a', x - 0.8, y - 34, 1.6, 34);
  block(c, x - 13, y - 36, 26, 9, '#1f5aa0', '#3a7ac0');
  fit(c, zh, x, y - 33, 4.2, '#ffffff', 24);
  fit(c, en, x, y - 29.2, 2.2, '#cfe6f2', 24);
}

function standTable(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 9, 2);
  rect(c, '#55606a', x - 0.8, y - 9, 1.6, 9);
  block(c, x - 8, y - 12, 16, 3, '#c8ccd0', '#e8ecef');
  for (const dx of [-5, 1]) {
    block(c, x + dx, y - 15.4, 4, 3.4, '#f4f1ea');
    rect(c, '#8a4a2a', x + dx + 0.6, y - 15, 2.8, 1);
  }
}

function ubike(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, x0 = d.x - w / 2, y = d.y;
  shadow(c, d.x, y, w / 2, 2.4);
  block(c, x0, y - 3, w, 3, '#8a9096');
  for (let i = 0; i < Math.floor(w / 10); i++) {
    const bx = x0 + 5 + i * 10;
    ellipse(c, '#2a2e33', bx - 3, y - 4, 2.6, 2.6);
    ellipse(c, '#2a2e33', bx + 3, y - 4, 2.6, 2.6);
    ellipse(c, '#8a9096', bx - 3, y - 4, 1.6, 1.6);
    ellipse(c, '#8a9096', bx + 3, y - 4, 1.6, 1.6);
    line(c, '#ffb020', 1, [bx - 3, y - 4, bx, y - 8, bx + 3, y - 4]);
    rect(c, '#ffb020', bx - 1, y - 9, 3.4, 1);
    block(c, x0 + 4 + i * 10, y - 16, 2, 10, '#c8ccd0');
  }
  txt(c, '微笑單車', d.x, y - 19, 3.6, '#ff8a00', 900);
}

/** Cars, buses, taxis and scooters streaming past (purely visual). */
function traffic(c: Ctx, d: Decor, t: number): void {
  const vert = d.variant === 0;
  const w = d.w ?? 128, h = d.h ?? 80;
  const cols = ['#ffd34e', '#f4f1ea', '#2b3440', '#d0453a', '#3a8fd9', '#9aa0a8', '#ffd34e'];
  c.save();
  if (vert) {
    const x0 = d.x - w / 2, y0 = d.y - h;
    c.beginPath();
    c.rect(x0, y0, w, h);
    c.clip();
    const lanes = [x0 + w * 0.13, x0 + w * 0.36, x0 + w * 0.64, x0 + w * 0.87];
    lanes.forEach((lx, li) => {
      const dir = li < 2 ? 1 : -1;
      const sp = 40 + li * 8;
      for (let k = 0; k < 4; k++) {
        const len = 960 + 160;
        const p = ((t * sp + k * 290 + li * 97) % len + len) % len - 80;
        const y = dir > 0 ? p : 960 - p;
        if (y < y0 - 40 || y > y0 + h + 40) continue;
        vehicle(c, lx, y, true, cols[(k + li * 2) % cols.length], (k + li) % 5, dir);
      }
    });
  } else {
    const x0 = d.x - w / 2, y0 = d.y;
    c.beginPath();
    c.rect(x0, y0, w, h);
    c.clip();
    const lanes = [y0 + h * 0.2, y0 + h * 0.4, y0 + h * 0.62, y0 + h * 0.84];
    const span = 1500;
    lanes.forEach((ly, li) => {
      const dir = li < 2 ? -1 : 1;
      const sp = 36 + li * 7;
      for (let k = 0; k < 5; k++) {
        const p = ((t * sp + k * 330 + li * 131) % span + span) % span - 60;
        const x = dir > 0 ? p : span - 120 - p;
        if (x < x0 - 40 || x > x0 + w + 40) continue;
        vehicle(c, x, ly, false, cols[(k * 3 + li) % cols.length], (k + li * 2) % 5, dir);
      }
    });
  }
  c.restore();
}

/** kind 0-1 car, 2 taxi (yellow), 3 bus, 4 scooter. */
function vehicle(c: Ctx, x: number, y: number, vert: boolean, col: string, kind: number, dir: number): void {
  const night = isNight();
  if (kind === 4) {
    // Scooter with a rider (seen from above).
    if (vert) {
      ellipse(c, 'rgba(0,0,0,0.25)', x + 1, y + 1, 2.6, 6);
      block(c, x - 1.6, y - 5, 3.2, 10, col);
      ellipse(c, '#1b1d24', x, y, 2.6, 2.4);
      ellipse(c, ['#e2493b', '#f4f1ea', '#3a8fd9'][Math.abs(Math.round(x)) % 3], x, y - 0.6, 2, 2);
    } else {
      ellipse(c, 'rgba(0,0,0,0.25)', x + 1, y + 1.4, 6, 2.4);
      block(c, x - 5, y - 1.6, 10, 3.2, col);
      ellipse(c, '#1b1d24', x, y - 2, 2.4, 2.6);
      ellipse(c, ['#e2493b', '#f4f1ea', '#3a8fd9'][Math.abs(Math.round(y)) % 3], x - 0.4, y - 3, 2, 2);
    }
    if (night) ellipse(c, 'rgba(255,240,200,0.35)', vert ? x : x + dir * 8, vert ? y + dir * 8 : y, 3, 3);
    return;
  }
  const bus = kind === 3;
  const L = bus ? 40 : 22, Wd = bus ? 13 : 11;
  const body = kind === 2 ? '#ffd23a' : bus ? '#2a8a5a' : col;
  if (vert) {
    ellipse(c, 'rgba(0,0,0,0.28)', x + 1.4, y + 1.6, Wd / 2 + 1, L / 2 + 1);
    block(c, x - Wd / 2, y - L / 2, Wd, L, body, shade(body, 0.2), shade(body, -0.3));
    rect(c, '#2a3a4a', x - Wd / 2 + 1, y + (dir > 0 ? L / 2 - 5 : -L / 2 + 2), Wd - 2, 3);
    rect(c, '#3a4a5a', x - Wd / 2 + 1, y + (dir > 0 ? -L / 2 + 2 : L / 2 - 4.4), Wd - 2, 2.4);
    if (bus) for (let i = -L / 2 + 6; i < L / 2 - 6; i += 4) rect(c, '#bfe6f2', x - Wd / 2 + 0.6, y + i, 0.8, 2.6);
    if (kind === 2) block(c, x - 2, y - 1.4, 4, 2.8, '#f4f1ea');
    if (night) {
      const fy = y + dir * (L / 2 + 6);
      ellipse(c, 'rgba(255,240,200,0.3)', x, fy, Wd * 0.7, 6);
      rect(c, '#ff3a3a', x - Wd / 2 + 0.6, y - dir * L / 2 - 0.6, 1.4, 1.2);
      rect(c, '#ff3a3a', x + Wd / 2 - 2, y - dir * L / 2 - 0.6, 1.4, 1.2);
    }
  } else {
    ellipse(c, 'rgba(0,0,0,0.28)', x + 1.6, y + 1.4, L / 2 + 1, Wd / 2 + 1);
    block(c, x - L / 2, y - Wd / 2 - 3, L, Wd + 3, body, shade(body, 0.2), shade(body, -0.3));
    rect(c, '#2a3a4a', x + (dir > 0 ? L / 2 - 6 : -L / 2 + 2), y - Wd / 2 - 2, 4, Wd - 1);
    rect(c, '#3a4a5a', x + (dir > 0 ? -L / 2 + 1.6 : L / 2 - 4.6), y - Wd / 2 - 2, 3, Wd - 1);
    if (bus) for (let i = -L / 2 + 6; i < L / 2 - 6; i += 4) rect(c, '#bfe6f2', x + i, y - Wd / 2 - 2.4, 2.6, 2);
    if (kind === 2) block(c, x - 1.4, y - Wd / 2 - 4.6, 2.8, 2, '#f4f1ea');
    ellipse(c, '#1b1d24', x - L / 2 + 3, y + Wd / 2, 1.4, 1);
    ellipse(c, '#1b1d24', x + L / 2 - 3, y + Wd / 2, 1.4, 1);
    if (night) {
      ellipse(c, 'rgba(255,240,200,0.3)', x + dir * (L / 2 + 7), y, 7, Wd * 0.6);
      rect(c, '#ff3a3a', x - dir * L / 2 - 0.6, y - 1, 1.2, 1.4);
    }
  }
}

function rainbow(c: Ctx, d: Decor): void {
  // The rainbow crossing at Exit 6 (彩虹斑馬線), painted flat on the road.
  const w = d.w ?? 80, h = d.h ?? 80, x0 = d.x - w / 2, y0 = d.y;
  const cols = ['#e8473a', '#ff9a2a', '#ffd23a', '#4ac06a', '#3a8fd9', '#8a4ad0'];
  for (let j = 0; j < h; j += 6) {
    const col = cols[(j / 6) % cols.length];
    c.fillStyle = col;
    c.globalAlpha = 0.85;
    c.fillRect(x0 + 2, y0 + j + 1, w - 4, 4);
  }
  c.globalAlpha = 1;
}

function railing(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, x0 = d.x - w / 2, y = d.y;
  for (let x = x0 + 2; x < x0 + w; x += 16) rect(c, '#e8e6e0', x, y - 9, 1.4, 9);
  rect(c, '#f4f1ea', x0, y - 9, w, 1.4);
  rect(c, '#ffd23a', x0, y - 6, w, 1.2);
  rect(c, 'rgba(0,0,0,0.2)', x0, y - 7.6, w, 0.4);
}

function busStop(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 26, 3);
  rect(c, '#55606a', x - 24, y - 26, 1.6, 26);
  rect(c, '#55606a', x + 22, y - 26, 1.6, 26);
  block(c, x - 26, y - 30, 52, 5, '#2a8a5a', '#3aa06a');
  vgrad(c, x - 22, y - 24, 44, 16, 'rgba(200,230,240,0.55)', 'rgba(160,200,220,0.35)');
  block(c, x - 18, y - 8, 30, 3, '#8a9096');
  block(c, x + 14, y - 24, 8, 16, '#f4f1ea');
  txt(c, '公車', x + 18, y - 20, 3, '#2a8a5a', 900);
  for (let i = 0; i < 4; i++) rect(c, ['#e2493b', '#3a8fd9', '#ffd34e', '#2a8a5a'][i], x + 15, y - 17 + i * 2.4, 6, 1.4);
  txt(c, '西門町', x - 4, y - 27.6, 3.4, '#ffffff', 900);
}

/** MRT exit canopy. `mouthSouth`: Ximen Exit 6 faces you; Longshan's exit is seen from the back. */
function mrtExit(c: Ctx, r: Rect, mouthSouth: boolean, label: string, en: string, num: string): void {
  const x0 = r.x - 2, x1 = r.x + r.w + 2, base = r.y + r.h + 2, w = x1 - x0, cx = (x0 + x1) / 2;
  const night = isNight();
  shadow(c, cx, base, w / 2 + 4, 4, 0.3, 30);
  const top = base - 40;
  // Glass side walls and the steel frame.
  vgrad(c, x0, top + 6, w, 34, 'rgba(190,225,240,0.75)', 'rgba(140,190,215,0.6)');
  for (const fx of [x0, x0 + w / 3, x0 + (2 * w) / 3, x1 - 1.6]) rect(c, '#8a9096', fx, top + 6, 1.6, 34);
  if (mouthSouth) {
    // The escalator going down into the station.
    vgrad(c, x0 + 6, top + 12, w - 12, 28, '#2a2e33', '#8a9096');
    for (let j = top + 14; j < base; j += 3) rect(c, 'rgba(255,255,255,0.18)', x0 + 7, j, w - 14, 0.6);
    rect(c, '#1b1d24', x0 + 5, top + 12, 2, 28);
    rect(c, '#1b1d24', x1 - 7, top + 12, 2, 28);
    rect(c, '#4a4e54', cx - 0.8, top + 12, 1.6, 28);
  } else {
    block(c, x0 + 4, top + 14, w - 8, 18, '#e8ecef');
    txt(c, label, cx, top + 20, 6, '#1f3a6a', 900);
    txt(c, en, cx, top + 27, 2.6, '#55606a', 700);
  }
  // Curved canopy roof.
  c.fillStyle = '#c8ccd0';
  c.beginPath();
  c.moveTo(x0 - 3, top + 8);
  c.quadraticCurveTo(cx, top - 4, x1 + 3, top + 8);
  c.lineTo(x1 + 3, top + 11);
  c.lineTo(x0 - 3, top + 11);
  c.closePath();
  c.fill();
  rect(c, '#8a9096', x0 - 3, top + 10, w + 6, 1.4);
  rect(c, 'rgba(255,255,255,0.5)', x0, top + 4, w, 0.6);
  // Exit number plate and the metro logo.
  block(c, cx - 9, top - 1, 18, 9, '#1f6fc0', '#3a8fe0', '#14508e');
  ellipse(c, '#ffffff', cx - 5, top + 3.4, 2.8, 2.8);
  ellipse(c, '#1f6fc0', cx - 5, top + 3.4, 1.7, 1.7);
  txt(c, num, cx + 3, top + 3.6, 6.4, '#ffffff', 900);
  if (mouthSouth) {
    block(c, cx - 16, top + 10.6, 32, 5, '#1b1d24');
    txt(c, `${label} ${en}`, cx, top + 13.2, 3, '#ffd34e', 900);
  }
  if (night) {
    ellipse(c, 'rgba(180,220,255,0.25)', cx, top + 20, w * 0.6, 16);
    ellipse(c, 'rgba(120,190,255,0.35)', cx, top + 3, 12, 6);
  }
}

function mapBoard(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 12, 2.4);
  rect(c, '#55606a', x - 9, y - 10, 1.6, 10);
  rect(c, '#55606a', x + 7.4, y - 10, 1.6, 10);
  block(c, x - 13, y - 30, 26, 21, '#1f3a6a', '#2a4a8a');
  rect(c, '#f4f1ea', x - 11.6, y - 25, 23.2, 14.6);
  // Tiny district map: streets, the Red House, Exit 6 star.
  rect(c, '#c8ccd0', x - 11, y - 20, 22, 1.4);
  rect(c, '#c8ccd0', x - 2, y - 25, 1.6, 14.6);
  rect(c, '#55606a', x - 11.6, y - 14.4, 23.2, 1.6);
  rect(c, '#c0392b', x + 3, y - 12.6, 4, 2);
  ellipse(c, '#ffd34e', x - 4, y - 16, 1.4, 1.4);
  txt(c, '西門町導覽', x, y - 27.4, 3.4, '#ffffff', 900);
}

// --- Food and fun counters ----------------------------------------------------------------

function vendor(c: Ctx, x: number, y: number, look: CharLook, t: number, facing: Facing = 'down'): void {
  drawCharacter(c, look, x, y, { facing, phase: 0, moving: false, blink: t % 4 < 0.1, breath: (t * 0.5) % 1 });
}
const TEEN_STAFF: CharLook = { skin: '#f6cfa8', hair: '#1d1a22', hairStyle: 'cap', hat: '#7a3a1a', top: '#7a3a1a', topShade: '#5a2a10', bottom: '#1d1a22', shoes: '#f4f1ea', apron: '#f4e2c4' };
const UNCLE: CharLook = { skin: '#d9a47a', hair: '#9a948a', hairStyle: 'short', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f3442', shoes: '#1d1814', apron: '#3a8fd9' };

function meesuaStall(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 3, 3.4, 0.35);
  // Two giant steaming pots of mee sua.
  for (const dx of [8, 26]) {
    block(c, x + dx - 7, y - 12, 14, 10, '#8a9096', '#c8ccd0', '#55606a');
    ellipse(c, '#7a3a1a', x + dx, y - 12, 6.4, 2.2);
    ellipse(c, '#a85a2a', x + dx - 1, y - 12.4, 4, 1.2);
    steam(c, x + dx, y - 14, t + dx, 3, 0.5);
  }
  line(c, '#55606a', 0.8, [x + 12, y - 13, x + 16, y - 22]);
  block(c, x, y - 2, w, h + 2, '#c0392b', '#e05a48', '#8a2a1e');
  txt(c, '大腸麵線', x + w / 2, y + 2.6, 5, '#fff3b0', 900, 'rgba(0,0,0,0.4)');
  // Stack of paper bowls and the cilantro tub.
  for (let i = 0; i < 4; i++) ellipse(c, '#f4f1ea', x + w - 5, y - 3 - i * 1.4, 3, 1);
  ellipse(c, '#3f9a3a', x + 3, y - 3, 2.4, 1.2);
}

function chickenCounter(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  vendor(c, x + w / 2, y - 3, UNCLE, t);
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3);
  block(c, x, y - 4, w, h + 4, '#f0a020', '#ffc040', '#b07010');
  // Hot oil fryer and the face-sized chicken cutlets.
  block(c, x + 2, y - 10, 12, 7, '#55606a', '#8a9096');
  ellipse(c, '#c8901a', x + 8, y - 10, 5, 1.6);
  for (let i = 0; i < 3; i++) ellipse(c, `rgba(255,240,200,${0.5 - i * 0.12})`, x + 5 + i * 3, y - 10.4 - ((t * 3 + i) % 1) * 2, 0.7, 0.7);
  ellipse(c, '#c8761a', x + w - 8, y - 9, 6, 4.4);
  ellipse(c, '#e8a040', x + w - 9, y - 10, 3.4, 2);
  txt(c, '雞排', x + w / 2, y + 1.6, 4.4, '#ffffff', 900, 'rgba(0,0,0,0.4)');
}

function iceCounter(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  vendor(c, x + 10, y - 3, { ...TEEN_STAFF, hat: '#2a8ad0', top: '#2a8ad0', topShade: '#1a6aa0' }, t);
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3);
  block(c, x, y - 4, w, h + 4, '#e0f2fa', '#ffffff', '#9ac8e0');
  // A towering mango shaved ice and a strawberry one.
  for (const [dx, col] of [[w - 22, '#ffb030'], [w - 9, '#ff6a8a']] as const) {
    block(c, x + dx - 5, y - 7, 10, 3, '#ffffff');
    ellipse(c, '#fffaf0', x + dx, y - 9, 5, 4);
    for (let i = 0; i < 5; i++) rect(c, col, x + dx - 3.4 + (i % 3) * 2.4, y - 12 + Math.floor(i / 3) * 2, 2, 2);
    ellipse(c, 'rgba(255,255,255,0.6)', x + dx - 1.4, y - 11.4, 1.4, 0.8);
  }
  txt(c, '芒果冰', x + w / 2, y + 1.6, 4.4, '#2a8ad0', 900);
  void t;
}

function teaCounter(c: Ctx, r: Rect, t: number): void {
  const { x, y, w, h } = r;
  vendor(c, x + w / 2, y - 3, TEEN_STAFF, t);
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3);
  block(c, x, y - 4, w, h + 4, '#7a3a1a', '#9a5a2a', '#5a2a10');
  for (let i = 0; i < 4; i++) {
    const cx = x + 4 + i * 6.4;
    block(c, cx - 2, y - 11, 4, 7, 'rgba(255,250,240,0.95)');
    rect(c, ['#c49a6c', '#e8a0b0', '#9ad07a', '#f0c870'][i], cx - 1.6, y - 8, 3.2, 3.6);
    for (let k = 0; k < 2; k++) ellipse(c, '#2a1810', cx - 0.8 + k * 1.6, y - 5, 0.5, 0.5);
    rect(c, '#e2493b', cx + 0.4, y - 13, 0.5, 3);
  }
  // The shaker doing its job.
  const sh = Math.sin(t * 18) * 1.2;
  block(c, x + w - 6 + sh, y - 14, 4, 8, '#c8ccd0', '#e8ecef');
  txt(c, '珍珠奶茶', x + w / 2, y + 1.6, 4.2, '#ffe08a', 900);
}

function clawRow(c: Ctx, r: Rect, t: number): void {
  const { x, y, w } = r;
  const n = Math.floor(w / 15);
  const night = isNight();
  for (let i = 0; i < n; i++) {
    const mx = x + 1 + i * (w / n), mw = w / n - 2;
    shadow(c, mx + mw / 2, y + r.h, mw / 2, 2.4);
    const col = ['#ff4aa8', '#4ac8ff', '#ffd23a', '#8a5aff', '#5ad08a'][i % 5];
    block(c, mx, y - 24, mw, 24 + r.h, col, shade(col, 0.25), shade(col, -0.3));
    vgrad(c, mx + 1.6, y - 21, mw - 3.2, 13, night ? 'rgba(240,250,255,0.95)' : 'rgba(210,235,250,0.9)', 'rgba(170,210,235,0.8)');
    // Plush toys inside.
    for (let p = 0; p < 4; p++) {
      const px2 = mx + 3 + p * ((mw - 6) / 3), py = y - 9.6 - (p % 2);
      ellipse(c, ['#ff9fc0', '#ffd34e', '#9fd6ff', '#c8a0ff', '#ffffff'][(p + i) % 5], px2, py, 2, 1.8);
      ellipse(c, '#1b1d24', px2 - 0.6, py - 0.3, 0.25, 0.25);
      ellipse(c, '#1b1d24', px2 + 0.6, py - 0.3, 0.25, 0.25);
    }
    // The claw drifting back and forth.
    const cx = mx + mw / 2 + Math.sin(t * 0.9 + i * 1.7) * (mw / 2 - 4);
    const drop = Math.max(0, Math.sin(t * 0.6 + i * 2.3)) * 5;
    rect(c, '#55606a', mx + 1.6, y - 21, mw - 3.2, 0.8);
    line(c, '#8a9096', 0.4, [cx, y - 20.4, cx, y - 18 + drop]);
    line(c, '#c8ccd0', 0.6, [cx - 1.6, y - 15 + drop, cx, y - 17.6 + drop, cx + 1.6, y - 15 + drop]);
    rect(c, '#1b1d24', mx + mw / 2 - 2, y - 6, 4, 2);
    ellipse(c, '#ff3a3a', mx + mw / 2 + 2.6, y - 5, 0.9, 0.9);
    if (night) ellipse(c, `${col}44`, mx + mw / 2, y - 14, mw * 0.7, 14);
  }
}

function boxOffice(c: Ctx, r: Rect, t: number): void {
  const { x, y, w } = r;
  vendor(c, x + w / 2, y - 6, { skin: '#f3c9a0', hair: '#1d1a22', hairStyle: 'long', top: '#c0392b', topShade: '#8a2a1e', bottom: '#1d1a22', shoes: '#1d1814', badge: '#ffd34e' }, t);
  shadow(c, x + w / 2, y + r.h, w / 2 + 2, 3);
  block(c, x - 1, y - 24, w + 2, 6, '#c0392b', '#e05a48');
  txt(c, '售票 TICKETS', x + w / 2, y - 21, 3.4, '#ffd34e', 900);
  vgrad(c, x + 2, y - 18, w - 4, 8, 'rgba(210,235,250,0.4)', 'rgba(170,210,235,0.3)');
  block(c, x, y - 10, w, 10 + r.h, '#8a2a1e', '#a8402a');
  // Showtimes board.
  block(c, x + w + 2, y - 22, 10, 18, '#1b1d24');
  for (let i = 0; i < 4; i++) {
    rect(c, ['#ffd34e', '#4ae0ff', '#ff8ac0', '#9affc0'][i], x + w + 3.4, y - 20 + i * 4, 3, 1.2);
    rect(c, '#f4f1ea', x + w + 7.4, y - 20 + i * 4, 3, 1.2);
  }
}

function posterStand(c: Ctx, r: Rect): void {
  const { x, y, w } = r;
  for (let i = 0; i < 3; i++) {
    const px2 = x + 1 + i * (w / 3), pw = w / 3 - 2;
    shadow(c, px2 + pw / 2, y + r.h, pw / 2, 1.6);
    rect(c, '#55606a', px2 + pw / 2 - 0.5, y - 4, 1, 4 + r.h);
    block(c, px2 - 0.6, y - 26, pw + 1.2, 22, '#e8c860');
    poster(c, px2, y - 25.4, pw, 20.8, 3 + i);
  }
}

function sneakerRack(c: Ctx, r: Rect): void {
  const { x, y, w } = r;
  shadow(c, x + w / 2, y + r.h, w / 2, 2.4);
  block(c, x, y - 10, w, 10 + r.h, '#e8e6e2', '#ffffff', '#b8b4ac');
  goods(c, 'sneakers', x + 1, y - 9.4, w - 2, 12, 7);
  block(c, x + w / 2 - 6, y - 15, 12, 5, '#1b1d24');
  txt(c, 'NEW!', x + w / 2, y - 12.4, 3, '#4ae0ff', 900);
}

function clothesRack(c: Ctx, r: Rect, t: number): void {
  const { x, y, w } = r;
  shadow(c, x + w / 2, y + r.h, w / 2, 2.4);
  rect(c, '#8a9096', x + 1, y - 22, 1, 22 + r.h);
  rect(c, '#8a9096', x + w - 2, y - 22, 1, 22 + r.h);
  rect(c, '#c8ccd0', x, y - 22, w, 1);
  const cols = ['#ff6aa0', '#3a8fd9', '#ffd34e', '#1b1d24', '#57c278', '#f4f1ea', '#9b5fc0'];
  for (let i = 0; i < 7; i++) {
    const cx = x + 4 + i * ((w - 8) / 6), sw = Math.sin(t * 1.4 + i) * 0.4;
    poly(c, cols[i], [cx - 2.4 + sw, y - 20.6, cx + 2.4 + sw, y - 20.6, cx + 3 + sw, y - 18.4, cx + 2 + sw, y - 18, cx + 2 + sw, y - 11, cx - 2 + sw, y - 11, cx - 2 + sw, y - 18, cx - 3 + sw, y - 18.4]);
  }
  block(c, x + w / 2 - 7, y - 8, 14, 6, '#e2493b');
  txt(c, 'SALE', x + w / 2, y - 5, 3.6, '#ffffff', 900);
}

function tattooBoard(c: Ctx, r: Rect): void {
  const { x, y, w } = r;
  shadow(c, x + w / 2, y + r.h, w / 2, 2);
  poly(c, '#1b1d24', [x + 2, y + r.h, x + 5, y - 20, x + w - 5, y - 20, x + w - 2, y + r.h]);
  block(c, x + 5, y - 19, w - 10, 18, '#111111');
  txt(c, '刺青', x + w / 2, y - 15.4, 4.6, '#ff3a3a', 900);
  // A rose and a little dragon from the flash sheet.
  ellipse(c, '#e2493b', x + w / 2 - 4, y - 8, 2, 1.8);
  line(c, '#3a8a3a', 0.5, [x + w / 2 - 4, y - 6.4, x + w / 2 - 4, y - 3.6]);
  line(c, '#3ac8a0', 0.8, [x + w / 2 + 1, y - 4, x + w / 2 + 3, y - 9, x + w / 2 + 5, y - 5, x + w / 2 + 6, y - 9]);
  txt(c, '18+', x + w / 2, y - 1.8, 2.6, '#ffd34e', 900);
}

function stickerBooth(c: Ctx, r: Rect, t: number): void {
  const { x, y, w } = r;
  shadow(c, x + w / 2, y + r.h, w / 2 + 3, 3);
  block(c, x - 2, y - 30, w + 4, 30 + r.h, '#ff8ac8', '#ffb0dc', '#d05a98');
  block(c, x, y - 34, w, 6, '#ffffff');
  txt(c, '大頭貼', x + w / 2, y - 31, 4.4, '#ff3a9a', 900);
  // The curtain sways; cute sample photos on the side.
  const sw = Math.sin(t * 1.2) * 0.8;
  for (let i = 0; i < 5; i++) rect(c, i % 2 ? '#ffe0f0' : '#ffc0e0', x + 3 + i * 3.6 + sw * (i / 5), y - 24, 3.4, 20);
  for (let i = 0; i < 3; i++) {
    block(c, x + w - 3, y - 26 + i * 7, 6, 6, '#ffffff');
    ellipse(c, '#f6cfa8', x + w, y - 23.6 + i * 7, 1.6, 1.6);
    ellipse(c, ['#ffd34e', '#9fd6ff', '#ff6aa0'][i], x + w + 1.6, y - 25 + i * 7, 0.8, 0.8);
  }
  // Sparkles.
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.8 + i / 3) % 1;
    rect(c, `rgba(255,255,255,${1 - k})`, x + 2 + i * 7, y - 33 - k * 4, 0.8, 0.8);
  }
}

/** The dancer by day, a singer with a guitar in the evening, an empty chalk circle at night. */
function busker(c: Ctx, r: Rect, t: number): void {
  const cx = r.x + r.w / 2, fy = r.y + r.h - 2;
  const tm = now();
  // Chalk circle.
  c.strokeStyle = 'rgba(255,255,255,0.35)';
  c.lineWidth = 0.6;
  c.beginPath();
  c.ellipse(cx, fy - 2, 22, 9, 0, 0, Math.PI * 2);
  c.stroke();
  if (tm >= 10 * 60 && tm < 17 * 60 + 30) {
    // Speaker and a bouncing dancer (popping and spinning).
    block(c, cx + 16, fy - 12, 7, 10, '#1b1d24', '#3a3e44');
    ellipse(c, '#55606a', cx + 19.5, fy - 8, 2.4, 2.4);
    ellipse(c, '#1b1d24', cx + 19.5, fy - 8, 1.2 + Math.abs(Math.sin(t * 8)) * 0.6, 1.2 + Math.abs(Math.sin(t * 8)) * 0.6);
    const beat = Math.floor(t * 2) % 8;
    const facing: Facing = (['down', 'left', 'up', 'right', 'down', 'down', 'right', 'left'] as Facing[])[beat];
    const hop = Math.abs(Math.sin(t * Math.PI * 2)) * 2.2;
    const look: CharLook = { skin: '#d9a47a', hair: '#1d1a22', hairStyle: 'cap', hat: '#ffd34e', top: '#ff3a8a', topShade: '#c02a6a', bottom: '#1d1a22', shoes: '#f4f1ea' };
    ellipse(c, 'rgba(20,10,6,0.3)', cx, fy, 4, 1.4);
    drawCharacter(c, look, cx, fy - hop, { facing, phase: (t * 2) % 1, moving: true, blink: false, breath: 0 });
    // Arms up on the beat.
    if (beat % 2 === 0) {
      line(c, '#d9a47a', 1, [cx - 3, fy - hop - 13, cx - 6, fy - hop - 19]);
      line(c, '#d9a47a', 1, [cx + 3, fy - hop - 13, cx + 6, fy - hop - 19]);
    }
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.7 + i / 3) % 1;
      txt(c, i % 2 ? '♪' : '♫', cx + 19 + Math.sin(t * 2 + i) * 4, fy - 14 - k * 16, 4, `rgba(255,220,120,${1 - k})`, 900);
    }
  } else if (tm >= 17 * 60 + 30 && tm < 22 * 60) {
    // Singer with a guitar, a mic stand, an amp, and an open guitar case for coins.
    const look: CharLook = { skin: '#f3c9a0', hair: '#3a2418', hairStyle: 'long', top: '#1b1d24', topShade: '#0e1014', bottom: '#3a4a7a', shoes: '#1d1814' };
    block(c, cx - 22, fy - 10, 8, 9, '#2a2e33', '#4a4e54');
    ellipse(c, '#1b1d24', cx - 18, fy - 5.4, 2.4, 2.4);
    rect(c, '#55606a', cx - 7, fy - 20, 0.8, 20);
    ellipse(c, '#2a2e33', cx - 6.6, fy - 20.6, 1.2, 1.4);
    drawCharacter(c, look, cx, fy, { facing: 'down', phase: 0, moving: false, blink: t % 3 < 0.1, breath: (t * 0.6) % 1 });
    // Guitar strummed across the body.
    ellipse(c, '#c8761a', cx + 1.4, fy - 7.6, 3.6, 2.8);
    ellipse(c, '#1b1410', cx + 1.4, fy - 7.6, 1, 1);
    line(c, '#5a3a20', 1.2, [cx + 3, fy - 8.4, cx + 9, fy - 13]);
    line(c, '#f4e2c4', 0.3, [cx - 0.6 + Math.sin(t * 10) * 0.4, fy - 9, cx + 2.6, fy - 6.6]);
    block(c, cx + 8, fy - 3, 12, 4, '#2a1810');
    rect(c, '#c0392b', cx + 9, fy - 2.4, 10, 2.6);
    for (let i = 0; i < 4; i++) ellipse(c, '#ffd34e', cx + 10.4 + i * 2.4, fy - 1.4, 0.7, 0.5);
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.5 + i / 3) % 1;
      txt(c, '♪', cx - 4 + Math.sin(t * 1.4 + i * 2) * 6, fy - 22 - k * 14, 4.4, `rgba(255,255,255,${1 - k})`, 900);
    }
  } else {
    txt(c, '街頭藝人', cx, fy - 2, 3.6, 'rgba(255,255,255,0.45)', 900);
  }
}

function plaqueArt(c: Ctx, x: number, y: number, v: number): void {
  // Flat on the ground: anchored at its top edge so people walk over it.
  if (v === 0) {
    ellipse(c, '#6a5020', x, y + 8, 7.4, 4.4);
    ellipse(c, '#c8a040', x, y + 8, 6.6, 3.8);
    ellipse(c, '#e8c860', x - 1, y + 7.4, 5, 2.6);
    txt(c, '西門町', x, y + 8.2, 3.4, '#6a4a10', 900);
  } else {
    block(c, x - 8, y + 3, 16, 9, '#1b1d24');
    rect(c, '#ff3a8a', x - 7, y + 4, 14, 0.6);
    txt(c, 'Ximen', x, y + 7.8, 4, '#ffffff', 900);
    rect(c, '#2ad0ff', x - 7, y + 10.4, 14, 0.6);
  }
}

// --- The Red House --------------------------------------------------------------------------

function brickFill(c: Ctx, x: number, y: number, w: number, h: number, col: string): void {
  rect(c, col, x, y, w, h);
  for (let j = y + 1.6; j < y + h; j += 2.2) {
    rect(c, 'rgba(255,220,190,0.14)', x, j, w, 0.35);
    const off = Math.floor((j - y) / 2.2) % 2 ? 2.2 : 0;
    for (let i = x + off; i < x + w; i += 4.4) rect(c, 'rgba(60,20,10,0.18)', i, j - 1.8, 0.35, 1.8);
  }
}

function archWindow(c: Ctx, x: number, y: number, w: number, h: number, night: boolean): void {
  ellipse(c, '#f4ead8', x + w / 2, y + 1, w / 2 + 1.2, w / 2 + 0.6);
  rect(c, '#f4ead8', x - 1.2, y + 1, w + 2.4, h);
  ellipse(c, night ? '#ffd890' : '#3a4a5a', x + w / 2, y + 1.4, w / 2, w / 2 - 0.4);
  vgrad(c, x, y + 1.4, w, h - 1.6, night ? '#ffd890' : '#4a5a6a', night ? '#ffb860' : '#2a3a4a');
  rect(c, '#f4ead8', x + w / 2 - 0.3, y - 1, 0.6, h + 1);
  rect(c, '#f4ead8', x, y + h * 0.5, w, 0.5);
}

function redHouse(c: Ctx, d: Decor): void {
  const x = d.x, base = d.y;
  const night = isNight();
  cachedArt(c, `redhouse:${night ? 1 : 0}`, x - 84, base - 112, 168, 116, (cc) => {
    shadow(cc, x, base, 78, 5, 0.32, 40);
    const fw = 62, sw = 34; // front face width, side faces' apparent width
    const fl = x - fw / 2, fr = x + fw / 2;
    const top = base - 60;
    // Side faces (angled, darker), then the front face.
    for (const s of [-1, 1]) {
      const ox = s < 0 ? fl - sw : fr;
      cc.save();
      cc.beginPath();
      if (s < 0) cc.rect(ox, top + 6, sw, 54);
      else cc.rect(ox, top + 6, sw, 54);
      cc.clip();
      brickFill(cc, ox, top + 6, sw, 54, '#8a3420');
      cc.restore();
      for (let i = 0; i < 2; i++) {
        archWindow(cc, ox + 6 + i * 16, top + 14, 7, 12, night);
        archWindow(cc, ox + 6 + i * 16, top + 38, 7, 12, night);
      }
      rect(cc, 'rgba(0,0,0,0.18)', ox, top + 6, sw, 54);
    }
    brickFill(cc, fl, top + 2, fw, 58, '#a8442a');
    // White stone bands, pilasters and the cornice.
    rect(cc, '#f4ead8', fl - sw, top + 30, fw + sw * 2, 2.4);
    rect(cc, '#f4ead8', fl - sw, top + 6, fw + sw * 2, 2);
    for (const px2 of [fl, fr - 3]) rect(cc, '#e8dcc4', px2, top + 2, 3, 58);
    // Front: arched windows upstairs, the entrance arch with a curved pediment below.
    for (let i = 0; i < 4; i++) archWindow(cc, fl + 7 + i * 13.4, top + 13, 6, 13, night);
    archWindow(cc, fl + 8, top + 37, 7, 13, night);
    archWindow(cc, fr - 15, top + 37, 7, 13, night);
    ellipse(cc, '#f4ead8', x, base - 16, 13, 11);
    vgrad(cc, x - 10.6, base - 17, 21.2, 17, night ? '#ffcf8a' : '#3a2a20', night ? '#ffa860' : '#1b1410');
    cc.fillStyle = night ? '#ffcf8a' : '#3a2a20';
    cc.beginPath();
    cc.ellipse(x, base - 16, 10.6, 9.6, 0, Math.PI, 0);
    cc.fill();
    // The name plaque.
    block(cc, x - 15, top + 31.6, 30, 7, '#f4ead8', '#ffffff');
    txt(cc, '西門紅樓', x, top + 35.2, 5.4, '#a8442a', 900);
    // The octagonal roof seen from above: eight facets rising to a little lantern and a flagpole.
    const ry = 13, rx = fw / 2 + sw + 3, cyR = top + 2, apexY = top - 24;
    const vx: number[] = [], vy: number[] = [];
    for (let i = 0; i < 8; i++) {
      const a = Math.PI / 8 + (i * Math.PI) / 4;
      vx.push(x + Math.cos(a) * rx);
      vy.push(cyR - Math.sin(a) * ry + 4);
    }
    for (let i = 0; i < 8; i++) {
      const j = (i + 1) % 8;
      const front = vy[i] + vy[j] > cyR * 2 + 8;
      const lit = (vx[i] + vx[j]) / 2 < x;
      poly(cc, front ? (lit ? '#5a6068' : '#40464e') : lit ? '#4a5058' : '#353a42', [x, apexY, vx[i], vy[i], vx[j], vy[j]]);
      line(cc, '#2a3038', 0.5, [x, apexY, vx[i], vy[i]]);
    }
    for (let i = 0; i < 8; i++) {
      const j = (i + 1) % 8;
      if (vy[i] + vy[j] > cyR * 2 + 8) line(cc, '#e8dcc4', 1.4, [vx[i], vy[i], vx[j], vy[j]]);
    }
    // The lantern on top: a tiny octagon of red brick and white trim.
    block(cc, x - 7, apexY - 12, 14, 12, '#a8442a');
    for (let i = 0; i < 3; i++) archWindow(cc, x - 5.4 + i * 4.2, apexY - 9.6, 2.2, 6, night);
    rect(cc, '#e8dcc4', x - 8, apexY - 13, 16, 1.4);
    poly(cc, '#3a4048', [x - 9, apexY - 12, x, apexY - 20, x + 9, apexY - 12]);
    rect(cc, '#c8ccd0', x - 0.3, apexY - 28, 0.6, 9);
    poly(cc, '#e2493b', [x + 0.3, apexY - 28, x + 6, apexY - 26.4, x + 0.3, apexY - 24.8]);
    if (night) {
      ellipse(cc, 'rgba(255,170,100,0.18)', x, base - 30, 80, 34);
      for (let i = 0; i < 9; i++) ellipse(cc, '#fff0b0', fl - sw + 4 + i * ((fw + sw * 2 - 8) / 8), top + 7.4, 0.8, 0.8);
    }
  });
}

function crossHall(c: Ctx, d: Decor): void {
  const x = d.x, base = d.y, w = d.w ?? 256;
  const night = isNight();
  cachedArt(c, `crosshall:${w}:${night ? 1 : 0}`, x - w / 2 - 4, base - 88, w + 8, 92, (cc) => {
    const x0 = x - w / 2, x1 = x + w / 2;
    shadow(cc, x, base, w / 2 + 4, 4, 0.3, 30);
    // Long two-storey brick hall with a gable at the crossing.
    brickFill(cc, x0, base - 50, w, 50, '#a8442a');
    rect(cc, '#f4ead8', x0, base - 27, w, 2);
    rect(cc, '#f4ead8', x0, base - 51, w, 2);
    for (let i = x0 + 8; i < x1 - 8; i += 16) {
      if (Math.abs(i + 4 - x) < 14) continue;
      archWindow(cc, i, base - 46, 7, 13, night);
      archWindow(cc, i, base - 22, 7, 14, night);
    }
    // Gable front of the cross arm, with a round window.
    poly(cc, '#a8442a', [x - 18, base - 51, x, base - 72, x + 18, base - 51]);
    poly(cc, '#f4ead8', [x - 20, base - 50, x, base - 74, x + 20, base - 50, x + 17, base - 50, x, base - 70, x - 17, base - 50]);
    ellipse(cc, '#f4ead8', x, base - 58, 5, 5);
    ellipse(cc, night ? '#ffd890' : '#3a4a5a', x, base - 58, 3.8, 3.8);
    brickFill(cc, x - 14, base - 50, 28, 50, '#b04a2e');
    rect(cc, '#e8dcc4', x - 14, base - 50, 2, 50);
    rect(cc, '#e8dcc4', x + 12, base - 50, 2, 50);
    archWindow(cc, x - 4, base - 44, 8, 14, night);
    // Doorway to the shops inside (the 16 creative workshops).
    vgrad(cc, x - 6, base - 18, 12, 18, night ? '#ffcf8a' : '#3a2a20', night ? '#ffa860' : '#1b1410');
    block(cc, x - 13, base - 27, 26, 5, '#f4ead8');
    txt(cc, '十字樓', x, base - 24.4, 3.6, '#a8442a', 900);
    // Grey tiled roof with ridge.
    poly(cc, '#4a5058', [x0 - 3, base - 50, x0 + 8, base - 64, x1 - 8, base - 64, x1 + 3, base - 50]);
    for (let j = base - 63; j < base - 50; j += 2.6) rect(cc, 'rgba(255,255,255,0.08)', x0, j, w, 0.5);
    rect(cc, '#2a3038', x0 + 8, base - 65, w - 16, 1.6);
    if (night) ellipse(cc, 'rgba(255,170,100,0.16)', x, base - 30, w * 0.55, 30);
  });
}

function cafeTable(c: Ctx, d: Decor): void {
  // Outdoor café table under a big umbrella (the Red House's terrace).
  const x = d.x, y = d.y;
  const col = ['#e2493b', '#2a8a7a', '#f4f1ea'][(d.variant ?? 0) % 3];
  shadow(c, x, y, 12, 3);
  for (const s of [-1, 1]) block(c, x + s * 8 - 2, y - 6, 4, 6, '#3a3e44');
  rect(c, '#55606a', x - 0.6, y - 26, 1.2, 26);
  block(c, x - 6, y - 9, 12, 3, '#f4f1ea');
  block(c, x - 2, y - 12, 2.6, 3, '#ffffff');
  poly(c, col, [x - 15, y - 24, x, y - 32, x + 15, y - 24]);
  for (let i = -15; i < 15; i += 5) poly(c, i % 10 === 0 ? shade(col, -0.15) : col, [x + i, y - 24, x + i + 2.5, y - 22.4, x + i + 5, y - 24]);
}

const BILLBOARDS: [string, string, string, string][] = [
  ['演唱會', 'LIVE CONCERT 7/20', '#2a0a4a', '#ff5ad0'],
  ['太空貓', 'NOW SHOWING', '#1b2a5a', '#ffd34e'],
  ['24H 好鄰居', 'OPEN ALL NIGHT', '#2a9a5a', '#ffffff'],
  ['球鞋特賣', 'SNEAKER SALE −50%', '#e2493b', '#ffffff'],
  ['動漫展', 'ANIME FAIR', '#ffd34e', '#c0306a'],
  ['歡唱ＫＴＶ', 'SING ALL NIGHT', '#5a1a8a', '#ffd34e'],
];
/** Rooftop billboard on a steel frame (lit by spotlights at night). */
function billboard(c: Ctx, d: Decor): void {
  const v = (d.variant ?? 0) % BILLBOARDS.length;
  const [zh, en, bg, fg] = BILLBOARDS[v];
  const w = d.w ?? 72, x0 = d.x - w / 2, base = d.y;
  const night = isNight();
  cachedArt(c, `bb:${v}:${w}:${night ? 1 : 0}`, x0 - 2, base - 46, w + 4, 48, (cc) => {
    shadow(cc, d.x, base, w / 2, 3, 0.25);
    for (const fx of [x0 + 6, x0 + w / 2 - 1, x0 + w - 8]) {
      rect(cc, '#4a4e54', fx, base - 16, 2, 16);
      line(cc, '#5a5e64', 0.5, [fx, base - 2, fx + 6, base - 14]);
    }
    rect(cc, '#3a3e44', x0 + 2, base - 16, w - 4, 1.4);
    block(cc, x0, base - 44, w, 28, '#2a2e33');
    vgrad(cc, x0 + 1.5, base - 42.5, w - 3, 25, bg, shade(bg, -0.25));
    fit(cc, zh, d.x, base - 34, 11, fg, w - 8, 'rgba(0,0,0,0.4)');
    fit(cc, en, d.x, base - 23, 4, '#ffffff', w - 8);
    if (night) {
      for (let i = 0; i < 3; i++) {
        const lx = x0 + 10 + i * ((w - 20) / 2);
        rect(cc, '#2a2e33', lx - 1.4, base - 15.4, 2.8, 2);
        poly(cc, 'rgba(255,250,220,0.16)', [lx - 1, base - 15, lx - 12, base - 44, lx + 12, base - 44, lx + 1, base - 15]);
      }
    }
  });
}

function redSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 20, 2.4);
  block(c, x - 22, y - 16, 44, 14, '#a8442a', '#c85a3a');
  rect(c, '#f4ead8', x - 21, y - 15, 42, 1);
  txt(c, '西門紅樓', x, y - 11, 6, '#f4ead8', 900);
  txt(c, 'THE RED HOUSE · since 1908', x, y - 5.6, 2.6, '#ffe0c0', 700);
  rect(c, '#6a2a1a', x - 22, y - 2, 44, 2);
}

function market(c: Ctx, r: Rect, t: number): void {
  // Weekend creative market: white tents, handmade goods, bunting.
  const { x, y, w } = r;
  const n = 3, tw = w / n;
  line(c, '#8a9096', 0.4, [x - 4, y - 34, x + w + 4, y - 34]);
  for (let i = 0; i < 14; i++) poly(c, ['#e2493b', '#ffd34e', '#3a8fd9', '#57c278'][i % 4], [x - 2 + i * (w + 4) / 14, y - 34, x + 1 + i * (w + 4) / 14, y - 34, x - 0.5 + i * (w + 4) / 14, y - 30.4 + Math.sin(t * 2 + i) * 0.3]);
  for (let i = 0; i < n; i++) {
    const tx = x + i * tw, cx = tx + tw / 2;
    shadow(c, cx, y + r.h, tw / 2, 3);
    rect(c, '#c8ccd0', tx + 1, y - 22, 1, 22 + r.h);
    rect(c, '#c8ccd0', tx + tw - 2, y - 22, 1, 22 + r.h);
    poly(c, '#f4f1ea', [tx - 1, y - 20, cx, y - 30, tx + tw + 1, y - 20]);
    rect(c, '#e8e6e0', tx - 1, y - 20, tw + 2, 2);
    block(c, tx + 1, y - 8, tw - 2, 8 + r.h, '#a8784a', '#c8986a');
    rect(c, ['#f4e2c4', '#e0f0e0', '#f0e0f0'][i], tx + 1.4, y - 9, tw - 2.8, 2);
    goods(c, (['sparkle', 'books', 'teaJars'] as const)[i], tx + 2, y - 15, tw - 4, 7, i + 30);
  }
  txt(c, '創意市集', x + w / 2, y - 37, 4, '#a8442a', 900);
}



// ---------------------------------------------------------------------------
// Longshan Temple
// ---------------------------------------------------------------------------

const TILE_A = '#b8642a', TILE_D = '#6a2a14';
const POR = ['#ff5a8a', '#ffd23a', '#3ac8a0', '#4a9ae0', '#ffffff', '#ff8a2a', '#9ae04a'];

/** Swallowtail roof section: tiled slope from eave (y1) to ridge (y0), split tips curling up. */
function swallowRoof(c: Ctx, xl: number, xr: number, y0: number, y1: number, seed: number, night: boolean): void {
  const inset = (y1 - y0) * 0.5;
  // Eave overhang shadow.
  rect(c, 'rgba(0,0,0,0.3)', xl + 2, y1, xr - xl - 4, 2);
  // Slope with tile rows.
  c.fillStyle = TILE_A;
  c.beginPath();
  c.moveTo(xl - 4, y1 - 2);
  c.quadraticCurveTo(xl + 2, y1 - 1, xl + inset * 0.4, y0 + 4);
  c.lineTo(xr - inset * 0.4, y0 + 4);
  c.quadraticCurveTo(xr - 2, y1 - 1, xr + 4, y1 - 2);
  c.lineTo(xr + 2, y1 + 1);
  c.lineTo(xl - 2, y1 + 1);
  c.closePath();
  c.fill();
  for (let j = y0 + 6; j < y1; j += 2.4) {
    const k = (j - y0) / (y1 - y0);
    rect(c, 'rgba(255,220,170,0.22)', xl + inset * 0.4 * (1 - k) + 1, j, xr - xl - inset * 0.8 * (1 - k) - 2, 0.5);
  }
  for (let i = xl + 4; i < xr - 4; i += 3) rect(c, 'rgba(90,30,10,0.22)', i, y0 + 5, 0.5, y1 - y0 - 5);
  rect(c, TILE_D, xl - 3, y1 - 1, xr - xl + 6, 1.6);
  // Upturned eave corners.
  poly(c, TILE_D, [xl - 4, y1 - 2, xl - 9, y1 - 7, xl - 2, y1 - 3.4]);
  poly(c, TILE_D, [xr + 4, y1 - 2, xr + 9, y1 - 7, xr + 2, y1 - 3.4]);
  // The ridge: a fat band of cut-porcelain (剪黏) in bright colours.
  const rl = xl + inset * 0.4 - 2, rr = xr - inset * 0.4 + 2;
  block(c, rl, y0 - 1, rr - rl, 6, '#2a8a7a', '#3ab09a', '#1a5a50');
  for (let i = rl + 2; i < rr - 2; i += 2.2) ellipse(c, POR[Math.floor(hash(Math.round(i), seed) * POR.length)], i, y0 + 2, 0.7, 0.9);
  rect(c, '#ffd23a', rl, y0 - 1.4, rr - rl, 0.7);
  // Swallowtail ends: the ridge sweeps up and splits like a swallow's tail.
  for (const s of [-1, 1]) {
    const ex = s < 0 ? rl : rr;
    c.fillStyle = '#2a8a7a';
    c.beginPath();
    c.moveTo(ex, y0 + 5);
    c.quadraticCurveTo(ex + s * 12, y0 + 5, ex + s * 16, y0 - 16);
    c.lineTo(ex + s * 13, y0 - 7);
    c.lineTo(ex + s * 21, y0 - 11);
    c.quadraticCurveTo(ex + s * 11, y0 + 1, ex, y0 - 1);
    c.closePath();
    c.fill();
    line(c, '#ffd23a', 0.7, [ex, y0 - 1, ex + s * 10, y0 - 1, ex + s * 16, y0 - 16]);
    line(c, '#ff5a8a', 0.5, [ex + s * 4, y0 + 2, ex + s * 12, y0 + 1, ex + s * 20, y0 - 10.6]);
    ellipse(c, '#ff5a8a', ex + s * 16.4, y0 - 16.6, 1.2, 1.2);
  }
  // Little porcelain figures standing on the ridge.
  const nf = Math.floor((rr - rl) / 22);
  for (let i = 0; i < nf; i++) {
    const fx = rl + (i + 0.5) * ((rr - rl) / nf), fy = y0 - 1;
    const col = POR[(i + seed) % POR.length];
    poly(c, col, [fx - 2, fy, fx - 1.2, fy - 5, fx + 1.2, fy - 5, fx + 2, fy]);
    ellipse(c, '#f8d8bc', fx, fy - 6.2, 1.2, 1.2);
    rect(c, POR[(i + seed + 2) % POR.length], fx - 1.4, fy - 7.8, 2.8, 0.8);
  }
  if (night) for (let i = rl; i < rr; i += 6) ellipse(c, '#fff0b0', i, y0 - 1.6, 0.6, 0.6);
}

/** A green-and-gold dragon undulating along a ridge, facing `dir`. */
function ridgeDragon(c: Ctx, x0: number, x1: number, y: number, dir: number): void {
  c.strokeStyle = '#3aa86a';
  c.lineWidth = 2.2;
  c.beginPath();
  for (let i = 0; i <= 20; i++) {
    const k = i / 20, x = x0 + (x1 - x0) * k;
    const yy = y - 3 - Math.sin(k * Math.PI * 2.5) * 2.6;
    if (i) c.lineTo(x, yy);
    else c.moveTo(x, yy);
  }
  c.stroke();
  c.strokeStyle = '#ffd23a';
  c.lineWidth = 0.6;
  c.stroke();
  const hx = dir > 0 ? x1 : x0;
  ellipse(c, '#3aa86a', hx, y - 4, 2.6, 2);
  ellipse(c, '#ff5a3a', hx + dir * 1.8, y - 3.4, 0.9, 0.7);
  line(c, '#ffd23a', 0.5, [hx, y - 5.6, hx - dir * 2, y - 8, hx - dir * 1, y - 5]);
  for (let i = 0; i < 4; i++) poly(c, '#ffd23a', [x0 + (x1 - x0) * (0.2 + i * 0.2), y - 5 - Math.sin((0.2 + i * 0.2) * Math.PI * 2.5) * 2.6, x0 + (x1 - x0) * (0.23 + i * 0.2), y - 8, x0 + (x1 - x0) * (0.26 + i * 0.2), y - 5]);
}

/** The flaming pearl the two dragons chase, on a little pagoda. */
function pearl(c: Ctx, x: number, y: number): void {
  block(c, x - 2, y - 6, 4, 6, '#ffd23a');
  ellipse(c, '#ff5a3a', x, y - 9, 3, 3);
  ellipse(c, '#ffd23a', x, y - 9, 1.6, 1.6);
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i - 2) * 0.5;
    poly(c, '#ff8a2a', [x + Math.cos(a) * 2.6, y - 9 + Math.sin(a) * 2.6, x + Math.cos(a) * 5.4, y - 9 + Math.sin(a) * 5.4, x + Math.cos(a + 0.2) * 2.6, y - 9 + Math.sin(a + 0.2) * 2.6]);
  }
}

/** Painted brackets (斗拱) and the coloured beam under an eave. */
function brackets(c: Ctx, xl: number, xr: number, y: number): void {
  block(c, xl, y, xr - xl, 4, '#1f5a8a');
  for (let i = xl + 2; i < xr - 2; i += 8) {
    rect(c, '#3ab09a', i, y + 0.8, 4, 2.4);
    rect(c, '#ffd23a', i + 4, y + 0.8, 4, 2.4);
  }
  for (let i = xl + 3; i < xr - 3; i += 6) {
    block(c, i, y - 3, 4, 3, i % 12 < 6 ? '#c0392b' : '#2a8a5a');
    rect(c, '#ffd23a', i + 0.6, y - 3.4, 2.8, 0.6);
  }
}

function stoneWindow(c: Ctx, cx: number, cy: number, r: number, night: boolean): void {
  ellipse(c, '#7a766e', cx, cy, r + 1.4, r + 1.4);
  ellipse(c, night ? '#4a3020' : '#2a2420', cx, cy, r, r);
  // Bamboo-node stone bars (竹節窗).
  for (let i = -r + 2; i < r - 1; i += 2.6) {
    const hh = Math.sqrt(Math.max(0, r * r - i * i));
    rect(c, '#a8a49a', cx + i - 0.6, cy - hh, 1.2, hh * 2);
    rect(c, '#c8c4ba', cx + i - 0.8, cy - 0.4, 1.6, 0.8);
  }
}

function carvedPanel(c: Ctx, x: number, y: number, w: number, h: number, seed: number): void {
  block(c, x, y, w, h, '#a8a49a', '#c8c4ba', '#7a766e');
  rect(c, '#8a867e', x + 1.4, y + 1.4, w - 2.8, h - 2.8);
  rect(c, '#b8b4aa', x + 2, y + 2, w - 4, h - 4);
  // A relief: clouds and a curling beast.
  c.strokeStyle = '#7a766e';
  c.lineWidth = 0.6;
  c.beginPath();
  c.moveTo(x + 3, y + h * 0.7);
  c.quadraticCurveTo(x + w * 0.3, y + h * (0.2 + hash(seed, 1) * 0.2), x + w * 0.5, y + h * 0.55);
  c.quadraticCurveTo(x + w * 0.7, y + h * 0.9, x + w - 3, y + h * 0.35);
  c.stroke();
  ellipse(c, '#8a867e', x + w * 0.5, y + h * 0.5, 1.4, 1.2);
}

function bronzePillar(c: Ctx, x: number, top: number, bottom: number): void {
  // 銅柱: a bronze column wrapped by a coiling dragon (Longshan's famous pair).
  const w = 7;
  block(c, x - w / 2 - 1.4, bottom - 4, w + 2.8, 4, '#8a867e', '#a8a49a');
  vgrad(c, x - w / 2, top, w, bottom - top - 4, '#5a4a30', '#3a2e1e');
  rect(c, 'rgba(255,230,160,0.25)', x - w / 2 + 1, top, 1, bottom - top - 4);
  rect(c, 'rgba(90,160,120,0.25)', x + w / 2 - 2, top, 1, bottom - top - 4);
  c.strokeStyle = '#a8904a';
  c.lineWidth = 1.6;
  c.beginPath();
  for (let i = 0; i <= 30; i++) {
    const k = i / 30, y = bottom - 6 - k * (bottom - top - 10);
    const xx = x + Math.sin(k * Math.PI * 5) * (w / 2 + 0.6);
    if (i) c.lineTo(xx, y);
    else c.moveTo(xx, y);
  }
  c.stroke();
  c.strokeStyle = '#d8c070';
  c.lineWidth = 0.5;
  c.stroke();
  ellipse(c, '#a8904a', x + 2, top + 5, 2.6, 2);
  ellipse(c, '#ff5a3a', x + 3.6, top + 4.6, 0.6, 0.5);
  block(c, x - w / 2 - 1, top - 2, w + 2, 3, '#8a867e');
}

function doorGod(c: Ctx, x: number, y: number, w: number, h: number, flip: number): void {
  // Painted guardian on a temple door leaf: helmet, red face, armour and a halberd.
  const cx = x + w / 2;
  ellipse(c, '#f0b080', cx, y + h * 0.24, w * 0.18, w * 0.16);
  rect(c, '#1b1410', cx - w * 0.2, y + h * 0.13, w * 0.4, h * 0.05);
  poly(c, '#ffd23a', [cx - w * 0.22, y + h * 0.15, cx, y + h * 0.05, cx + w * 0.22, y + h * 0.15]);
  poly(c, '#2a6a9a', [cx - w * 0.3, y + h * 0.34, cx + w * 0.3, y + h * 0.34, cx + w * 0.36, y + h * 0.9, cx - w * 0.36, y + h * 0.9]);
  rect(c, '#ffd23a', cx - w * 0.3, y + h * 0.5, w * 0.6, h * 0.04);
  rect(c, '#3a9a6a', cx - w * 0.36, y + h * 0.7, w * 0.72, h * 0.06);
  line(c, '#c8c4ba', 0.6, [cx + flip * w * 0.36, y + h * 0.1, cx + flip * w * 0.36, y + h * 0.9]);
  ellipse(c, '#c0392b', cx, y + h * 0.29, w * 0.08, w * 0.04);
}

function frontHall(c: Ctx, d: Decor, t: number): void {
  const W = d.w ?? 576, x0 = d.x - W / 2, base = d.y;
  const night = isNight();
  cachedArt(c, `fronthall:${W}:${night ? 1 : 0}`, x0 - 12, base - 112, W + 24, 116, (cc) => {
    shadow(cc, d.x, base, W / 2 + 6, 6, 0.3, 40);
    const X = (r: number) => x0 + r;
    // Side wings: red brick with an octagonal stone window and a small swallowtail roof.
    for (const [a, b2] of [[0, 64], [W - 64, W]]) {
      brickFill(cc, X(a), base - 40, b2 - a, 40, '#a8442a');
      rect(cc, '#8a867e', X(a), base - 8, b2 - a, 8);
      stoneWindow(cc, X((a + b2) / 2), base - 24, 9, night);
      swallowRoof(cc, X(a + 2), X(b2 - 2), base - 52, base - 40, a, night);
    }
    // Main body: granite base course and grey stone walls.
    block(cc, X(60), base - 4, W - 120, 4, '#9a968e', '#b8b4aa');
    block(cc, X(64), base - 50, W - 128, 46, '#c8c0b0', '#d8d2c4');
    const bays: [number, number, string][] = [[64, 128, 'wall'], [128, 160, 'tiger'], [160, 252, 'window'], [252, 324, 'centre'], [324, 416, 'window'], [416, 448, 'dragon'], [448, 512, 'wall']];
    for (const [a, b2, kind] of bays) {
      const bw = b2 - a;
      if (kind === 'wall') {
        carvedPanel(cc, X(a + 6), base - 44, bw - 12, 16, a);
        carvedPanel(cc, X(a + 6), base - 26, bw - 12, 18, a + 1);
      } else if (kind === 'window') {
        stoneWindow(cc, X(a + bw / 2), base - 30, 12, night);
        carvedPanel(cc, X(a + 8), base - 14, bw - 16, 10, a + 2);
        carvedPanel(cc, X(a + 8), base - 48, bw - 16, 5, a + 3);
      } else if (kind === 'centre') {
        // The central doors stay closed: they are for the gods.
        block(cc, X(a + 8), base - 42, bw - 16, 38, '#b8282a', '#d8402a', '#7a1a10');
        rect(cc, '#7a1a10', X(a + bw / 2) - 0.4, base - 42, 0.8, 38);
        doorGod(cc, X(a + 9), base - 41, bw / 2 - 9, 34, 1);
        doorGod(cc, X(a + bw / 2), base - 41, bw / 2 - 9, 34, -1);
        for (let j = 0; j < 5; j++) for (const s of [-1, 1]) ellipse(cc, '#ffd23a', X(a + bw / 2) + s * 3, base - 38 + j * 7, 0.7, 0.7);
        rect(cc, '#8a867e', X(a + 6), base - 6, bw - 12, 2);
      } else {
        // Open side door (dragon / tiger): the warm, smoky hall seen inside.
        vgrad(cc, X(a + 2), base - 40, bw - 4, 36, night ? '#ffcf8a' : '#3a2418', night ? '#a85a2a' : '#1b1008');
        ellipse(cc, night ? 'rgba(255,240,180,0.5)' : 'rgba(255,200,120,0.25)', X(a + bw / 2), base - 26, 6, 10);
        block(cc, X(a + 1), base - 41, 4, 37, '#b8282a', '#d8402a');
        block(cc, X(b2 - 5), base - 41, 4, 37, '#b8282a', '#d8402a');
        rect(cc, '#7a766e', X(a), base - 4, bw, 2);
        block(cc, X(a + 4), base - 49, bw - 8, 7, '#1b1410');
        txt(cc, kind === 'dragon' ? '龍門' : '虎門', X(a + bw / 2), base - 45.4, 4.6, '#ffd23a', 900);
      }
    }
    // Stone pillars between bays, and the two bronze dragon pillars at the centre.
    for (const r of [64, 128, 160, 448, 416, 512]) block(cc, X(r) - 2, base - 50, 4, 46, '#9a968e', '#b8b4aa');
    bronzePillar(cc, X(250), base - 50, base - 2);
    bronzePillar(cc, X(326), base - 50, base - 2);
    // Eave brackets, then the three-section roof (centre raised).
    brackets(cc, X(60), X(W - 60), base - 56);
    swallowRoof(cc, X(56), X(220), base - 84, base - 58, 3, night);
    swallowRoof(cc, X(356), X(520), base - 84, base - 58, 5, night);
    swallowRoof(cc, X(196), X(380), base - 96, base - 62, 7, night);
    ridgeDragon(cc, X(222), X(276), base - 96, 1);
    ridgeDragon(cc, X(300), X(354), base - 96, -1);
    pearl(cc, X(288), base - 97);
    ridgeDragon(cc, X(90), X(130), base - 84, 1);
    ridgeDragon(cc, X(446), X(486), base - 84, -1);
    // The name plaque over the centre door.
    block(cc, X(266), base - 56, 44, 9, '#1b1410', '#3a2a20');
    rect(cc, '#ffd23a', X(267), base - 55.2, 42, 0.5);
    txt(cc, '龍山寺', X(288), base - 51.4, 6.4, '#ffd23a', 900);
    if (night) {
      ellipse(cc, 'rgba(255,180,90,0.2)', d.x, base - 30, W * 0.42, 30);
      for (let i = X(64); i < X(W - 64); i += 8) ellipse(cc, '#fff0b0', i, base - 57, 0.7, 0.7);
    }
  });
  // Big red lanterns swaying under the eaves (live, so they glow and move).
  for (const r of [200, 236, 340, 376, 96, 480]) {
    const sw = Math.sin(t * 1.3 + r) * 0.8;
    lantern(c, x0 + r + sw, base - 46, r === 96 || r === 480 ? 3.4 : 4.4, night, '#d8282a', r === 96 || r === 480 ? undefined : '福');
  }
}

function mainHall(c: Ctx, d: Decor, t: number): void {
  const W = d.w ?? 320, x0 = d.x - W / 2, base = d.y;
  const night = isNight();
  cachedArt(c, `mainhall:${W}:${night ? 1 : 0}`, x0 - 14, base - 108, W + 28, 112, (cc) => {
    const X = (r: number) => x0 + r;
    shadow(cc, d.x, base, W / 2 + 4, 5, 0.3, 30);
    // Raised stone platform with steps.
    block(cc, X(0), base - 6, W, 6, '#9a968e', '#b8b4aa');
    for (let i = 0; i < 3; i++) rect(cc, '#c8c4ba', X(W / 2 - 30 + i * 2), base - 2 - i * 1.6, 60 - i * 4, 1.2);
    // The open hall: dim interior, Guanyin on her altar, candles and hanging lamps.
    vgrad(cc, X(8), base - 46, W - 16, 40, night ? '#7a4a2a' : '#2a1810', night ? '#c8803a' : '#4a2a18');
    block(cc, X(W / 2 - 22), base - 18, 44, 12, '#8a2a1e', '#b0402a');
    rect(cc, '#ffd23a', X(W / 2 - 22), base - 18, 44, 1);
    ellipse(cc, 'rgba(255,220,140,0.5)', X(W / 2), base - 32, 14, 14);
    ellipse(cc, '#e8b830', X(W / 2), base - 24, 6, 6);
    ellipse(cc, '#f0c840', X(W / 2), base - 33, 3.4, 3.8);
    poly(cc, '#e8b830', [X(W / 2 - 7), base - 18, X(W / 2 - 4), base - 30, X(W / 2 + 4), base - 30, X(W / 2 + 7), base - 18]);
    for (const s of [-1, 1]) {
      ellipse(cc, '#e8b830', X(W / 2 + s * 30), base - 24, 4, 4.6);
      ellipse(cc, '#f0c840', X(W / 2 + s * 30), base - 30, 2.4, 2.6);
      rect(cc, '#f4f1ea', X(W / 2 + s * 14), base - 24, 1.4, 6);
      ellipse(cc, '#ffb040', X(W / 2 + s * 14) + 0.7, base - 25, 0.8, 1.2);
    }
    // Red columns along the front.
    for (let i = 0; i <= 8; i++) {
      const px2 = X(8 + i * ((W - 16) / 8));
      block(cc, px2 - 2.6, base - 48, 5.2, 42, '#b8282a', '#d8402a', '#7a1a10');
      block(cc, px2 - 3.4, base - 9, 6.8, 3, '#8a867e');
      rect(cc, '#ffd23a', px2 - 2.6, base - 46, 5.2, 0.8);
    }
    // Lower eave, then the upper roof: a double-eaved hall.
    brackets(cc, X(4), X(W - 4), base - 52);
    swallowRoof(cc, X(0), X(W), base - 66, base - 54, 11, night);
    block(cc, X(30), base - 76, W - 60, 10, '#c8c0b0', '#d8d2c4');
    for (let i = X(36); i < X(W - 36); i += 14) {
      rect(cc, '#2a8a7a', i, base - 74, 8, 6);
      line(cc, '#ffd23a', 0.4, [i, base - 71, i + 8, base - 71]);
    }
    swallowRoof(cc, X(24), X(W - 24), base - 98, base - 76, 13, night);
    ridgeDragon(cc, X(W / 2 - 66), X(W / 2 - 10), base - 98, 1);
    ridgeDragon(cc, X(W / 2 + 10), X(W / 2 + 66), base - 98, -1);
    // Pagoda centrepiece on the ridge.
    for (let i = 0; i < 3; i++) block(cc, X(W / 2) - 4 + i, base - 102 - i * 3, 8 - i * 2, 3, i % 2 ? '#3ab09a' : '#ffd23a');
    ellipse(cc, '#ff5a3a', X(W / 2), base - 112 + 2, 1.4, 1.6);
    block(cc, X(W / 2 - 20), base - 64, 40, 8, '#1b1410');
    txt(cc, '圓通寶殿', X(W / 2), base - 60, 5, '#ffd23a', 900);
  });
  // Smoke drifting out of the hall.
  smoke(c, d.x, base - 30, t, 4, 30, 0.18, 1.4);
  for (const r of [0.22, 0.4, 0.6, 0.78]) lantern(c, x0 + W * r + Math.sin(t + r * 9) * 0.6, base - 44, 3.6, night);
}

function tower(c: Ctx, d: Decor, t: number): void {
  const x = d.x, base = d.y;
  const drum = d.variant === 1;
  const night = isNight();
  cachedArt(c, `tower:${drum ? 1 : 0}:${night ? 1 : 0}`, x - 40, base - 92, 80, 96, (cc) => {
    shadow(cc, x, base, 32, 4, 0.3, 30);
    // Stone base with an arched opening.
    block(cc, x - 28, base - 24, 56, 24, '#b8b4aa', '#c8c4ba', '#8a867e');
    for (let j = base - 22; j < base; j += 4) rect(cc, 'rgba(0,0,0,0.1)', x - 27, j, 54, 0.5);
    ellipse(cc, '#2a2420', x, base - 12, 7, 5);
    rect(cc, '#2a2420', x - 7, base - 12, 14, 12);
    // Pavilion with red posts and the bell (or drum) hanging inside.
    rect(cc, '#3a2418', x - 24, base - 46, 48, 22);
    for (const px2 of [-24, -8, 8, 24]) block(cc, x + px2 - 2, base - 46, 4, 22, '#b8282a', '#d8402a');
    if (drum) {
      ellipse(cc, '#c0392b', x, base - 35, 8, 6);
      ellipse(cc, '#f4e2c4', x, base - 35, 5.4, 4);
      for (let i = 0; i < 8; i++) ellipse(cc, '#ffd23a', x + Math.cos(i * 0.785) * 7, base - 35 + Math.sin(i * 0.785) * 5.2, 0.5, 0.5);
    } else {
      poly(cc, '#7a6a3a', [x - 4, base - 42, x + 4, base - 42, x + 6.4, base - 29, x - 6.4, base - 29]);
      rect(cc, '#a8904a', x - 6.4, base - 30, 12.8, 1.4);
      rect(cc, 'rgba(255,230,160,0.3)', x - 3, base - 41, 1, 11);
    }
    rect(cc, '#ffd23a', x - 26, base - 25, 52, 1);
    brackets(cc, x - 28, x + 28, base - 50);
    // The unusual sedan-chair (helmet) roof of Longshan's bell and drum towers.
    cc.fillStyle = TILE_A;
    cc.beginPath();
    cc.moveTo(x - 34, base - 52);
    cc.quadraticCurveTo(x - 20, base - 56, x - 16, base - 66);
    cc.quadraticCurveTo(x - 10, base - 80, x, base - 82);
    cc.quadraticCurveTo(x + 10, base - 80, x + 16, base - 66);
    cc.quadraticCurveTo(x + 20, base - 56, x + 34, base - 52);
    cc.closePath();
    cc.fill();
    for (let j = base - 78; j < base - 52; j += 2.6) {
      const k = (j - (base - 82)) / 30;
      rect(cc, 'rgba(255,220,170,0.22)', x - 6 - k * 26, j, 12 + k * 52, 0.5);
    }
    rect(cc, TILE_D, x - 34, base - 53, 68, 1.6);
    poly(cc, TILE_D, [x - 34, base - 52, x - 40, base - 58, x - 31, base - 54]);
    poly(cc, TILE_D, [x + 34, base - 52, x + 40, base - 58, x + 31, base - 54]);
    line(cc, '#2a8a7a', 1.4, [x - 16, base - 66, x, base - 82, x + 16, base - 66]);
    // Gourd finial.
    ellipse(cc, '#ffd23a', x, base - 84, 2.6, 2.2);
    ellipse(cc, '#ffd23a', x, base - 88, 1.6, 1.6);
    txt(cc, drum ? '鼓樓' : '鐘樓', x, base - 18, 4.4, '#7a1a10', 900);
  });
  lantern(c, x - 16 + Math.sin(t) * 0.5, base - 44, 2.8, night);
  lantern(c, x + 16 + Math.sin(t + 1) * 0.5, base - 44, 2.8, night);
}

function lanternString(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 128, x0 = d.x - w / 2;
  const street = d.variant === 1;
  const y = d.y - (street ? 40 : d.variant === 2 ? 46 : 58);
  const night = isNight();
  c.strokeStyle = '#2a1810';
  c.lineWidth = 0.4;
  c.beginPath();
  c.moveTo(x0, y);
  c.quadraticCurveTo(d.x, y + 6, x0 + w, y);
  c.stroke();
  const n = Math.max(3, Math.floor(w / (street ? 14 : 20)));
  for (let i = 0; i < n; i++) {
    const k = (i + 0.5) / n, lx = x0 + w * k;
    const ly = y + 4 * (1 - (2 * k - 1) ** 2) + 3 + Math.sin(t * 1.4 + i) * 0.4;
    lantern(c, lx, ly, street ? 2.6 : 3, night, street && i % 3 === 1 ? '#f0b020' : '#d8282a');
  }
}

function offerTable(c: Ctx, x: number, y: number, w: number, t: number): void {
  // Red-draped table loaded with fruit, flowers, cakes and candles.
  shadow(c, x, y, w / 2 + 2, 3);
  block(c, x - w / 2, y - 10, w, 10, '#b8282a', '#d8402a', '#7a1a10');
  rect(c, '#ffd23a', x - w / 2 + 1, y - 9, w - 2, 1);
  for (let i = x - w / 2 + 4; i < x + w / 2 - 2; i += 8) rect(c, '#ffd23a', i, y - 6, 3, 3);
  // Pineapple (旺來), oranges, bananas, a cake box, two vases of flowers.
  const top = y - 10;
  ellipse(c, '#e8a020', x - w * 0.3, top - 3, 2.6, 3.4);
  for (let k = 0; k < 3; k++) poly(c, '#3a8a3a', [x - w * 0.3 - 1.6 + k * 1.6, top - 6, x - w * 0.3 - 0.8 + k * 1.6, top - 10, x - w * 0.3 + k * 1.6, top - 6]);
  for (let k = 0; k < 4; k++) ellipse(c, '#ff8a1a', x - w * 0.08 + (k % 2) * 3, top - 2 - Math.floor(k / 2) * 2.6, 1.6, 1.6);
  ellipse(c, '#f4f1ea', x - w * 0.04, top - 0.4, 4, 1);
  poly(c, '#ffd23a', [x + w * 0.1, top - 1, x + w * 0.2, top - 6, x + w * 0.24, top - 5, x + w * 0.16, top - 1]);
  for (const s of [-1, 1]) {
    const vx = x + s * w * 0.42;
    block(c, vx - 1.4, top - 6, 2.8, 6, '#e8e6e0');
    for (let k = 0; k < 4; k++) ellipse(c, ['#ff5a8a', '#ffd23a', '#ffffff', '#ff8a2a'][k], vx + (k - 1.5) * 1.4, top - 8 - (k % 2) * 1.4, 1.1, 1.1);
    ellipse(c, '#3a8a3a', vx, top - 6.4, 2.4, 1);
  }
  const fl = Math.sin(t * 9) * 0.3;
  for (const s of [-0.18, 0.32]) {
    rect(c, '#c0392b', x + s * w - 0.6, top - 6, 1.2, 6);
    ellipse(c, '#ffd040', x + s * w + fl, top - 7, 0.7, 1.2);
    if (isNight()) ellipse(c, 'rgba(255,200,100,0.35)', x + s * w, top - 7, 3, 3);
  }
}

function slips(c: Ctx, d: Decor): void {
  // The fortune-slip cabinet: a hundred tiny drawers, one poem in each.
  const x = d.x, y = d.y;
  shadow(c, x, y, 14, 2.4);
  block(c, x - 13, y - 28, 26, 28, '#7a4a20', '#9a6a3a', '#4a2a10');
  for (let r = 0; r < 6; r++) for (let i = 0; i < 6; i++) {
    rect(c, '#9a6a3a', x - 11.6 + i * 4, y - 26.4 + r * 4.2, 3.4, 3.6);
    rect(c, '#e8c860', x - 10.3 + i * 4, y - 24.8 + r * 4.2, 0.8, 0.6);
  }
  block(c, x - 14, y - 31, 28, 4, '#b8282a');
  txt(c, '籤詩', x, y - 29, 3, '#ffd23a', 900);
}

function cushion(c: Ctx, d: Decor): void {
  ellipse(c, 'rgba(20,10,6,0.2)', d.x, d.y + 0.6, 6, 2.2);
  ellipse(c, '#b8282a', d.x, d.y, 5.4, 2);
  ellipse(c, '#d8402a', d.x, d.y - 0.4, 4, 1.2);
}

function rockery(c: Ctx, d: Decor, t: number): void {
  // The garden at the temple entrance: a rock cliff with a waterfall into the koi pond.
  const x = d.x, base = d.y, w = d.w ?? 64;
  cachedArt(c, `rockery:${w}`, x - w / 2 - 8, base - 70, w + 16, 74, (cc) => {
    const rocks: [number, number, number, number][] = [[-22, -8, 14, 12], [18, -10, 16, 13], [-12, -26, 15, 16], [10, -30, 14, 18], [-2, -46, 13, 15], [20, -44, 11, 12], [-24, -36, 10, 12], [4, -60, 9, 9]];
    for (const [dx, dy, rx, ry] of rocks) {
      ellipse(cc, '#5a5a56', x + dx, base + dy, rx, ry * 0.7);
      ellipse(cc, '#7a7a74', x + dx - rx * 0.2, base + dy - ry * 0.2, rx * 0.7, ry * 0.45);
      ellipse(cc, '#9a9a92', x + dx - rx * 0.35, base + dy - ry * 0.32, rx * 0.3, ry * 0.18);
    }
    // Ferns, moss and a little pine on the rocks.
    for (let i = 0; i < 10; i++) ellipse(cc, i % 2 ? '#3f7a35' : '#5a9a4a', x - 26 + hash(i, 3) * 52, base - 10 - hash(i, 4) * 50, 3, 2);
    rect(cc, '#5a3a20', x + 16, base - 58, 1.6, 10);
    ellipse(cc, '#2f6a3a', x + 17, base - 60, 7, 3);
    ellipse(cc, '#3f7a45', x + 13, base - 56, 5, 2.4);
  });
  // The falling water and its splash.
  const fx = x - 4;
  for (let i = 0; i < 5; i++) {
    const k = (t * 1.6 + i / 5) % 1;
    rect(c, `rgba(220,245,255,${0.7 - k * 0.4})`, fx - 3 + (i % 3) * 1.6, base - 48 + k * 40, 1.2, 6);
  }
  vgrad(c, fx - 4, base - 48, 8, 44, 'rgba(200,235,250,0.55)', 'rgba(160,215,240,0.3)');
  for (let i = 0; i < 4; i++) {
    const k = (t * 1.2 + i / 4) % 1;
    ellipse(c, `rgba(255,255,255,${0.6 * (1 - k)})`, fx, base - 2, 3 + k * 8, 1 + k * 2);
  }
}

function koi(c: Ctx, d: Decor, t: number): void {
  // Koi and lily pads in the entrance pond (flat, drawn over the water).
  const w = d.w ?? 96, h = d.h ?? 32, x0 = d.x - w / 2, y0 = d.y;
  for (let i = 0; i < 4; i++) ellipse(c, '#3f8a45', x0 + 8 + i * 22 + (i % 2) * 6, y0 + 8 + (i % 2) * 12, 4, 2.4);
  ellipse(c, '#ff8ac0', x0 + 10, y0 + 7, 1.4, 1);
  ellipse(c, '#ffffff', x0 + 58, y0 + 19, 1.4, 1);
  for (let i = 0; i < 5; i++) {
    const a = t * (0.35 + i * 0.05) + i * 1.3;
    const fx = x0 + w * 0.42 + Math.cos(a) * (w * 0.3 - i * 3), fy = y0 + h / 2 + Math.sin(a) * (h * 0.28);
    const dir = -Math.sin(a), dy = Math.cos(a);
    const col = ['#ff7a2a', '#ffffff', '#ffb030', '#e83a2a', '#ffe0c0'][i];
    ellipse(c, col, fx, fy, 2.6, 1.3);
    poly(c, col, [fx - dir * 2.4, fy - dy * 0.6, fx - dir * 4.6, fy - 1.4, fx - dir * 4.6, fy + 1.4]);
    if (i % 2 === 0) ellipse(c, '#e83a2a', fx + dir * 0.6, fy - 0.3, 0.9, 0.6);
  }
  const k = (t * 0.5) % 1;
  c.strokeStyle = `rgba(255,255,255,${0.4 * (1 - k)})`;
  c.lineWidth = 0.5;
  c.beginPath();
  c.ellipse(x0 + w * 0.7, y0 + h * 0.4, 2 + k * 8, 1 + k * 3, 0, 0, Math.PI * 2);
  c.stroke();
}

function stoneSign(c: Ctx, d: Decor): void {
  const x = d.x, base = d.y;
  cachedArt(c, 'stonesign', x - 34, base - 52, 68, 56, (cc) => {
    shadow(cc, x, base, 30, 3);
    // Shrubs and a stone lantern behind the name tablet.
    for (let i = 0; i < 6; i++) ellipse(cc, i % 2 ? '#3f7a35' : '#2f6a2f', x - 26 + i * 10, base - 6 - (i % 2) * 3, 7, 5);
    block(cc, x + 18, base - 30, 6, 26, '#9a968e', '#b8b4aa');
    block(cc, x + 15, base - 34, 12, 5, '#8a867e');
    rect(cc, '#ffd890', x + 19.4, base - 27, 3.2, 3);
    poly(cc, '#7a766e', [x + 13, base - 34, x + 21, base - 40, x + 29, base - 34]);
    // Granite tablet.
    block(cc, x - 20, base - 38, 34, 32, '#8a867e', '#a8a49a', '#5a5650');
    rect(cc, '#7a766e', x - 18, base - 36, 30, 28);
    txt(cc, '艋舺', x - 3, base - 30, 6, '#c0392b', 900);
    txt(cc, '龍山寺', x - 3, base - 20, 7, '#c0392b', 900);
    txt(cc, 'LONGSHAN TEMPLE', x - 3, base - 12, 2.6, '#f4f1ea', 700);
  });
}

function incenseStall(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 18, 3);
  block(c, x - 18, y - 10, 36, 10, '#7a4a20', '#9a6a3a');
  // Bundles of incense, candles, flowers and joss paper for sale.
  for (let i = 0; i < 4; i++) {
    const bx = x - 15 + i * 4;
    for (let k = 0; k < 4; k++) rect(c, k % 2 ? '#c0392b' : '#d8a040', bx + k * 0.7, y - 18, 0.5, 8);
  }
  for (let i = 0; i < 3; i++) ellipse(c, ['#ff5a8a', '#ffd23a', '#ffffff'][i], x + 2 + i * 3.4, y - 12, 1.4, 1.2);
  for (let i = 0; i < 3; i++) rect(c, '#e8c040', x + 10 + i * 1.6, y - 13 - i * 0.8, 5, 1.6);
  block(c, x - 10, y - 26, 20, 6, '#b8282a');
  txt(c, '香 · 花', x, y - 23, 3.6, '#ffd23a', 900);
}

// --- Wanhua streets ----------------------------------------------------------------------------

function herbPot(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 9, 2.6);
  if (v === 2) {
    // Heaps of fresh herb bundles on bamboo trays.
    ellipse(c, '#c8a060', x, y - 3, 8, 3);
    for (let i = 0; i < 6; i++) ellipse(c, ['#3f7a35', '#6a9a3a', '#4a8a4a'][i % 3], x - 5 + (i % 3) * 5, y - 5 - Math.floor(i / 3) * 3, 3, 2.2);
    return;
  }
  // A huge metal urn of herbal tea with a tap, or a copper cooking pot.
  block(c, x - 7, y - 20, 14, 18, v ? '#c8762a' : '#c8ccd0', v ? '#e8964a' : '#e8ecef', v ? '#8a4a1a' : '#8a9096');
  ellipse(c, v ? '#a85a1a' : '#9aa0a8', x, y - 20, 7, 2);
  rect(c, '#55606a', x - 8.4, y - 2, 16.8, 2);
  block(c, x + 5, y - 8, 4, 2, '#3a3e44');
  if (v === 0) {
    rect(c, '#2f7a3a', x - 6, y - 15, 12, 5);
    txt(c, '青草茶', x, y - 12.6, 3.4, '#fff6dc', 900);
  } else steam(c, x, y - 22, t, 3, 0.4);
}

function huaxiGate(c: Ctx, d: Decor, t: number): void {
  const x = d.x, base = d.y, w = d.w ?? 112;
  const night = isNight();
  cachedArt(c, `huaxi:${night ? 1 : 0}`, x - w / 2 - 12, base - 92, w + 24, 96, (cc) => {
    for (const s of [-1, 1]) {
      const px2 = x + s * (w / 2 - 6);
      shadow(cc, px2, base, 6, 2);
      block(cc, px2 - 4, base - 60, 8, 60, '#b8282a', '#d8402a', '#7a1a10');
      block(cc, px2 - 5, base - 6, 10, 6, '#8a867e');
      // Couplet panels.
      block(cc, px2 - 2.6, base - 54, 5.2, 40, '#1b1410');
      const words = s < 0 ? ['觀', '光', '夜', '市'] : ['華', '西', '老', '街'];
      words.forEach((ch, i) => txt(cc, ch, px2, base - 49 + i * 9.6, 4.4, '#ffd23a', 900));
    }
    // The top beam, the name board and a little dragon roof.
    block(cc, x - w / 2 - 4, base - 66, w + 8, 8, '#1f5a8a', '#2a6a9a');
    for (let i = x - w / 2; i < x + w / 2; i += 8) rect(cc, '#ffd23a', i, base - 64, 4, 3);
    block(cc, x - 34, base - 76, 68, 12, '#b8282a', '#d8402a');
    rect(cc, '#ffd23a', x - 33, base - 75, 66, 0.6);
    txt(cc, '華西街觀光夜市', x, base - 70, 7, '#ffd23a', 900, 'rgba(0,0,0,0.4)');
    swallowRoof(cc, x - w / 2 + 2, x + w / 2 - 2, base - 88, base - 76, 21, night);
    ridgeDragon(cc, x - 30, x - 4, base - 88, 1);
    ridgeDragon(cc, x + 4, x + 30, base - 88, -1);
    pearl(cc, x, base - 89);
  });
  for (let i = 0; i < 5; i++) lantern(c, x - w / 2 + 16 + i * ((w - 32) / 4), base - 52 + Math.sin(t * 1.2 + i) * 0.5, 3.4, night, i % 2 ? '#f0b020' : '#d8282a');
}

const STALLS: [string, string, Goods][] = [['蚵仔煎', '#e2493b', 'noodle'], ['甜湯', '#2f7a3a', 'tea'], ['彈珠台', '#3a8fd9', 'arcade'], ['水果', '#ff9f43', 'fruit']];
function eveningOpen(): boolean {
  const tm = now();
  return tm >= 17 * 60 || tm < 60;
}

function huaxiStall(c: Ctx, d: Decor, t: number): void {
  const [label, canopy, g] = STALLS[(d.variant ?? 0) % STALLS.length];
  const r = { x: d.x - 16, y: d.y - 12, w: 32, h: 12 };
  stall(c, r, t, label, canopy, (cc, rr) => goods(cc, g, rr.x + 2, rr.y - 20, rr.w - 4, 10, d.variant ?? 0), eveningOpen());
}

function oldLamp(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  const night = isNight();
  shadow(c, x, y, 3, 1.2, 0.3, 30);
  block(c, x - 2.4, y - 4, 4.8, 4, '#2a2e33');
  rect(c, '#2a2e33', x - 1, y - 30, 2, 26);
  line(c, '#2a2e33', 0.8, [x, y - 30, x + 5, y - 32, x + 6, y - 30]);
  poly(c, '#2a2e33', [x + 3, y - 30, x + 9, y - 30, x + 8, y - 28, x + 4, y - 28]);
  ellipse(c, night ? '#fff0b0' : '#e8e0c8', x + 6, y - 27, 2, 1.6);
  if (night) ellipse(c, 'rgba(255,220,140,0.3)', x + 6, y - 26, 8, 7);
}

function rickshaw(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 14, 2.4);
  ellipse(c, '#3a2418', x - 6, y - 6, 6, 6);
  ellipse(c, '#7a5a3a', x - 6, y - 6, 4.6, 4.6);
  for (let i = 0; i < 4; i++) line(c, '#3a2418', 0.4, [x - 6 + Math.cos(i * 0.785) * 4.6, y - 6 + Math.sin(i * 0.785) * 4.6, x - 6 - Math.cos(i * 0.785) * 4.6, y - 6 - Math.sin(i * 0.785) * 4.6]);
  block(c, x - 12, y - 18, 12, 10, '#2a2018', '#3a2a20');
  poly(c, '#1b1410', [x - 13, y - 18, x - 10, y - 26, x + 1, y - 26, x, y - 18]);
  line(c, '#5a3a20', 1, [x, y - 10, x + 14, y - 6]);
  line(c, '#5a3a20', 1, [x, y - 12, x + 14, y - 8]);
}

function chessTable(c: Ctx, x: number, y: number, t: number, players: boolean): void {
  // Stone table with a xiangqi board; old men on stools hunched over it.
  if (players) {
    vendor(c, x - 9, y - 4, { skin: '#d9a47a', hair: '#c9c4bc', hairStyle: 'short', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#3a3a3e', shoes: '#1d1814' }, t, 'right');
    vendor(c, x + 9, y - 4, { skin: '#c88a5a', hair: '#9a948a', hairStyle: 'cap', hat: '#3a5a8a', top: '#5a7a9a', topShade: '#3a5a7a', bottom: '#5b4636', shoes: '#1d1814', glasses: true }, t + 1.3, 'left');
  }
  shadow(c, x, y, 9, 2.4);
  block(c, x - 2.4, y - 7, 4.8, 7, '#8a867e');
  ellipse(c, '#9a968e', x, y - 8, 8, 4);
  ellipse(c, '#b8b4aa', x, y - 8.6, 7.4, 3.4);
  rect(c, '#e8d8b0', x - 5, y - 10.6, 10, 4);
  for (let i = 0; i < 5; i++) rect(c, '#8a5a30', x - 5 + i * 2.5, y - 10.6, 0.3, 4);
  rect(c, '#6a9ab0', x - 5, y - 8.8, 10, 0.5);
  for (let i = 0; i < 8; i++) ellipse(c, i % 2 ? '#c0392b' : '#1b1d24', x - 4.4 + (i % 4) * 2.8, y - 10 + Math.floor(i / 4) * 2.6, 0.8, 0.5);
  if (players && Math.floor(t / 3) % 3 === 0) txt(c, '將!', x + 10, y - 26, 4, '#c0392b', 900);
}

function banyan(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  const v = Math.floor(hash(Math.round(x), Math.round(y)) * 3);
  cachedArt(c, `banyan:${v}`, x - 34, y - 74, 68, 78, (cc) => {
    shadow(cc, x, y, 26, 6, 0.3, 30);
    // Thick trunk, buttress roots and hanging aerial roots.
    poly(cc, '#5a4632', [x - 6, y, x - 4, y - 30, x + 4, y - 30, x + 7, y]);
    poly(cc, '#4a3828', [x - 12, y, x - 6, y - 8, x - 3, y]);
    poly(cc, '#4a3828', [x + 12, y, x + 6, y - 8, x + 3, y]);
    for (let i = 0; i < 9; i++) {
      const rx = x - 24 + i * 6 + hash(i, v) * 3;
      line(cc, 'rgba(110,86,60,0.8)', 0.5, [rx, y - 40, rx + hash(i, 2) * 2 - 1, y - 40 + 14 + hash(v, i) * 20]);
    }
    const blobs: [number, number, number][] = [[-16, -48, 16], [12, -50, 17], [-2, -60, 16], [-24, -38, 11], [24, -40, 12], [0, -44, 14]];
    for (const [dx, dy, r] of blobs) ellipse(cc, '#2a5a2a', x + dx, y + dy, r, r * 0.72);
    for (const [dx, dy, r] of blobs) ellipse(cc, '#3a7a35', x + dx - 3, y + dy - 3, r * 0.75, r * 0.5);
    for (const [dx, dy, r] of blobs) ellipse(cc, '#5a9a45', x + dx - 6, y + dy - 6, r * 0.32, r * 0.2);
  });
  void t;
}

function toilet(c: Ctx, d: Decor): void {
  const x = d.x, base = d.y, w = d.w ?? 64;
  shadow(c, x, base, w / 2, 3);
  block(c, x - w / 2, base - 30, w, 30, '#e8e0d0', '#f4ecdc', '#b8b0a0');
  poly(c, '#4a5058', [x - w / 2 - 3, base - 30, x - w / 2 + 6, base - 38, x + w / 2 - 6, base - 38, x + w / 2 + 3, base - 30]);
  for (const s of [-1, 1]) {
    block(c, x + s * 14 - 5, base - 20, 10, 20, '#5a7a9a');
    ellipse(c, s < 0 ? '#3a8fd9' : '#e2493b', x + s * 14, base - 25, 2.4, 2.4);
  }
  txt(c, '廁所 WC', x, base - 34, 3.4, '#ffffff', 900);
}

// --- Longshan objects ---------------------------------------------------------------------------

function burner(c: Ctx, r: Rect, t: number): void {
  // 天公爐: the great bronze incense burner, bristling with sticks and wrapped in smoke.
  const x = r.x + r.w / 2, y = r.y + r.h;
  const night = isNight();
  shadow(c, x, y, 26, 5, 0.35, 30);
  for (const dx of [-14, 0, 14]) block(c, x + dx - 2.4, y - 10, 4.8, 10, '#3a2e1e', '#5a4a30');
  // Bowl with dragon handles.
  c.fillStyle = '#5a4a30';
  c.beginPath();
  c.moveTo(x - 22, y - 26);
  c.quadraticCurveTo(x - 22, y - 8, x, y - 8);
  c.quadraticCurveTo(x + 22, y - 8, x + 22, y - 26);
  c.closePath();
  c.fill();
  rect(c, 'rgba(255,230,160,0.25)', x - 16, y - 24, 3, 12);
  rect(c, 'rgba(90,160,120,0.3)', x + 10, y - 22, 4, 10);
  ellipse(c, '#6a5a3a', x, y - 26, 22, 4.4);
  ellipse(c, '#2a2018', x, y - 26, 19, 3.4);
  for (const s of [-1, 1]) {
    c.strokeStyle = '#a8904a';
    c.lineWidth = 1.6;
    c.beginPath();
    c.arc(x + s * 23, y - 20, 4, -Math.PI / 2, Math.PI / 2, s < 0);
    c.stroke();
  }
  // A forest of burning incense sticks.
  for (let i = 0; i < 26; i++) {
    const sx = x - 16 + hash(i, 5) * 32, sy = y - 26 + (hash(i, 6) - 0.5) * 4, h = 6 + hash(i, 7) * 6;
    line(c, i % 3 ? '#c0392b' : '#d8a040', 0.4, [sx, sy, sx + (hash(i, 8) - 0.5) * 2, sy - h]);
    ellipse(c, '#ff6a2a', sx + (hash(i, 8) - 0.5) * 2, sy - h, 0.4, 0.4);
  }
  ellipse(c, `rgba(255,120,40,${night ? 0.4 : 0.2})`, x, y - 30, 18, 6);
  // Roof-shaped lid on four posts above.
  for (const dx of [-15, 15]) rect(c, '#3a2e1e', x + dx - 1, y - 46, 2, 20);
  poly(c, '#4a3a24', [x - 24, y - 44, x - 12, y - 54, x + 12, y - 54, x + 24, y - 44]);
  rect(c, '#a8904a', x - 24, y - 45, 48, 1.4);
  poly(c, '#4a3a24', [x - 24, y - 44, x - 29, y - 49, x - 21, y - 46]);
  poly(c, '#4a3a24', [x + 24, y - 44, x + 29, y - 49, x + 21, y - 46]);
  ellipse(c, '#a8904a', x, y - 56, 2.4, 2.4);
  txt(c, '天公爐', x, y - 16, 4, '#d8c070', 900);
  smoke(c, x, y - 30, t, 9, 70, 0.4, 1.6);
}

function moonTable(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 12, 2.4);
  block(c, x - 12, y - 9, 24, 9, '#7a4a20', '#9a6a3a');
  block(c, x - 9, y - 12, 18, 3, '#b8282a');
  // Pairs of red crescent moon blocks (筊杯).
  for (let i = 0; i < 3; i++) {
    const bx = x - 6 + i * 6;
    c.fillStyle = '#d8282a';
    c.beginPath();
    c.ellipse(bx - 1, y - 13, 2, 1.2, 0, Math.PI, 0);
    c.fill();
    c.beginPath();
    c.ellipse(bx + 1.6, y - 13, 2, 1.2, 0, Math.PI, 0);
    c.fill();
  }
  // A pair lying on the floor in front, one flat side up, one round side up.
  ellipse(c, '#d8282a', x - 4, y + 4, 2.6, 1.2);
  ellipse(c, '#f0c8a0', x + 3, y + 4, 2.6, 1.2);
  ellipse(c, '#d8282a', x + 3, y + 3.6, 2.6, 0.6);
  void t;
}

function sticks(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 10, 2.4);
  block(c, x - 10, y - 8, 20, 8, '#7a4a20', '#9a6a3a');
  // Bamboo cylinder full of numbered sticks, gently rattled.
  const shake = Math.sin(t * 6) > 0.8 ? Math.sin(t * 40) * 0.5 : 0;
  block(c, x - 4 + shake, y - 20, 8, 12, '#c8a050', '#e8c070', '#8a6a30');
  rect(c, '#8a6a30', x - 4 + shake, y - 16, 8, 0.6);
  for (let i = 0; i < 10; i++) {
    const sx = x - 3 + i * 0.66 + shake;
    rect(c, '#e8d090', sx, y - 26 - (i % 3), 0.5, 7);
    rect(c, '#c0392b', sx, y - 26 - (i % 3), 0.5, 1);
  }
  txt(c, '求籤', x, y - 4, 3.4, '#ffd23a', 900);
}

function herbCounter(c: Ctx, r: Rect): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3);
  block(c, x, y - 4, w, h + 4, '#2f7a3a', '#3f9a4a', '#1f5a2a');
  // Two big tea urns, cups of dark herbal tea, a bundle of fresh herbs.
  for (const dx of [6, w - 6]) {
    block(c, x + dx - 4.4, y - 16, 8.8, 12, '#c8ccd0', '#e8ecef', '#8a9096');
    rect(c, '#3a3e44', x + dx + 3, y - 8, 2.4, 1.4);
  }
  for (let i = 0; i < 3; i++) {
    block(c, x + w / 2 - 6 + i * 4.4, y - 9, 3.4, 4.6, 'rgba(255,250,240,0.95)');
    rect(c, '#3a4a1a', x + w / 2 - 5.6 + i * 4.4, y - 7.6, 2.6, 3);
  }
  txt(c, '青草茶 NT$20', x + w / 2, y + 1.6, 3.6, '#fff6dc', 900);
}

function herbSign(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 5, 1.6, 0.3, 40);
  rect(c, '#5a3a20', x - 1, y - 44, 2, 44);
  block(c, x - 6, y - 46, 12, 34, '#2f7a3a', '#3f9a4a', '#1f5a2a');
  ['青', '草', '巷'].forEach((ch, i) => txt(c, ch, x, y - 40 + i * 10, 7, '#fff6dc', 900, 'rgba(0,0,0,0.3)'));
  for (let i = 0; i < 3; i++) ellipse(c, '#5a9a3a', x - 7 + i * 7, y - 47, 3, 2);
}

function historyBoard(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 12, 2.4);
  for (const s of [-1, 1]) rect(c, '#3a2418', x + s * 9 - 0.8, y - 12, 1.6, 12);
  block(c, x - 13, y - 30, 26, 19, '#3a2418', '#5a3a28');
  rect(c, '#f4e8c8', x - 11.6, y - 28.6, 23.2, 16);
  txt(c, '剝皮寮歷史街區', x, y - 26, 3, '#7a2a1a', 900);
  // A tiny old photo and a few lines of text.
  rect(c, '#8a7a60', x - 10, y - 23, 8, 8);
  rect(c, '#c8b898', x - 9, y - 22, 3, 6);
  for (let i = 0; i < 4; i++) rect(c, '#9a8a70', x, y - 22 + i * 2, 9, 0.6);
}

function hxStall(c: Ctx, r: Rect, t: number, label: string, canopy: string, g: Goods): void {
  stall(c, r, t, label, canopy, (cc, rr, tt) => {
    goods(cc, g, rr.x + 2, rr.y - 20, rr.w - 4, 10, 3);
    steam(cc, rr.x + rr.w / 2, rr.y - 12, tt, 3, 0.4);
  }, eveningOpen());
}

function pigeons(c: Ctx, r: Rect, t: number): void {
  // A flock that pecks around, then bursts into the air and lands again.
  const cycle = t % 22;
  const flying = cycle > 17;
  for (let i = 0; i < 11; i++) {
    const hx = r.x + hash(i, 1) * r.w, hy = r.y + hash(i, 2) * r.h;
    let x = hx + Math.sin(t * 0.3 + i) * 4, y = hy + Math.cos(t * 0.27 + i * 2) * 2, z = 0;
    if (flying) {
      const k = (cycle - 17) / 5;
      const a = k * Math.PI * 2 + i * 0.6;
      x = r.x + r.w / 2 + Math.cos(a) * (20 + i * 2);
      y = r.y + r.h / 2 + Math.sin(a) * 8;
      z = Math.sin(k * Math.PI) * 30 + 4;
    }
    ellipse(c, 'rgba(20,10,6,0.2)', x, y + 0.8, 2, 0.8);
    const by = y - z - (flying ? 0 : Math.max(0, Math.sin(t * 5 + i * 3)) * 0.8);
    const col = i % 4 === 0 ? '#e8e6e0' : i % 3 === 0 ? '#7a6a5a' : '#8a8f9a';
    ellipse(c, col, x, by - 1.6, 2.2, 1.4);
    if (flying) {
      const f = Math.sin(t * 20 + i) * 1.6;
      poly(c, shade(col, -0.1), [x - 0.6, by - 1.8, x - 3.4, by - 2.4 - f, x - 1.2, by - 1.2]);
      poly(c, shade(col, -0.1), [x + 0.6, by - 1.8, x + 3.4, by - 2.4 - f, x + 1.2, by - 1.2]);
    }
    const peck = !flying && Math.sin(t * 3 + i * 1.7) > 0.5 ? 1 : 0;
    ellipse(c, '#6a6f7a', x + (i % 2 ? 1.6 : -1.6), by - 2.6 + peck, 1, 1);
    rect(c, '#5ab07a', x + (i % 2 ? 1 : -1.4), by - 2, 0.6, 0.4);
    rect(c, '#e8a040', x + (i % 2 ? 2.4 : -2.8), by - 2.6 + peck, 0.5, 0.3);
  }
}

function canoe(c: Ctx, r: Rect): void {
  // 艋舺 comes from the Ketagalan word for "canoe": a bronze canoe on a granite plinth.
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 30, 3);
  block(c, x - 26, y - 8, 52, 8, '#8a867e', '#a8a49a');
  txt(c, '艋舺 BANGKA', x, y - 4, 3.4, '#f4f1ea', 900);
  c.fillStyle = '#6a5a3a';
  c.beginPath();
  c.moveTo(x - 30, y - 16);
  c.quadraticCurveTo(x, y - 6, x + 30, y - 16);
  c.lineTo(x + 26, y - 12);
  c.quadraticCurveTo(x, y - 6, x - 26, y - 12);
  c.closePath();
  c.fill();
  c.fillStyle = '#8a7a4a';
  c.beginPath();
  c.moveTo(x - 30, y - 16);
  c.quadraticCurveTo(x, y - 12, x + 30, y - 16);
  c.quadraticCurveTo(x, y - 9, x - 30, y - 16);
  c.fill();
  line(c, '#5a3a20', 1, [x + 6, y - 12, x + 14, y - 26]);
  ellipse(c, '#5a3a20', x + 15, y - 27, 1.4, 2.4);
}

function fortuneTable(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  const night = isNight();
  shadow(c, x, y, 13, 2.4);
  block(c, x - 12, y - 10, 24, 10, '#c0392b', '#d8504a');
  rect(c, '#ffd23a', x - 11, y - 9, 22, 0.6);
  txt(c, '☯', x - 6, y - 4.6, 4, '#ffd23a', 900);
  txt(c, '命理', x + 4, y - 4.6, 3.4, '#ffd23a', 900);
  // A banner on a pole and a little desk lamp.
  rect(c, '#5a3a20', x + 12, y - 36, 1, 36);
  block(c, x + 13, y - 36, 7, 20, '#f4ead8');
  ['卜', '卦', '算', '命'].forEach((ch, i) => txt(c, ch, x + 16.5, y - 33 + i * 4.6, 3.4, '#1b1410', 900));
  rect(c, '#3a3e44', x - 10, y - 16, 0.8, 6);
  poly(c, '#2a6a3a', [x - 13, y - 16, x - 7, y - 16, x - 8.4, y - 19, x - 11.6, y - 19]);
  if (night) ellipse(c, 'rgba(255,230,150,0.35)', x - 10, y - 13, 8, 4);
}

function oldIce(c: Ctx, r: Rect, t: number): void {
  const { x, y, w } = r;
  shadow(c, x + w / 2, y + r.h, w / 2 + 2, 2.4);
  block(c, x - 2, y - 14, w + 4, 14 + r.h, '#e8ecef', '#ffffff', '#9aa0a8');
  vgrad(c, x, y - 12, w, 8, 'rgba(200,235,250,0.9)', 'rgba(160,210,235,0.8)');
  for (let i = 0; i < 5; i++) ellipse(c, ['#ff8a2a', '#ffd34e', '#e2493b', '#7ad04a', '#ff6a8a'][i], x + 2 + i * (w - 4) / 4, y - 7, 1.6, 1.6);
  txt(c, '剉冰', x + w / 2, y - 2, 3.4, '#2a8ad0', 900);
  void t;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

registerPropDrawers({
  objects: {
    xm_exit6: (c, r) => mrtExit(c, r, true, '西門', 'Ximen', '6'),
    xm_mapBoard: (c, r) => mapBoard(c, r),
    xm_meesua: meesuaStall,
    xm_chicken: chickenCounter,
    xm_ice: iceCounter,
    xm_tea: teaCounter,
    xm_claw: clawRow,
    xm_cinema: boxOffice,
    xm_posters: (c, r) => posterStand(c, r),
    xm_sneakers: (c, r) => sneakerRack(c, r),
    xm_clothes: clothesRack,
    xm_tattoo: (c, r) => tattooBoard(c, r),
    xm_sticker: stickerBooth,
    xm_busker: busker,
    xm_plaque: (c, r) => plaqueArt(c, r.x + r.w / 2, r.y, 0),
    xm_market: market,
    xm_mrtL: (c, r) => mrtExit(c, r, false, '龍山寺站', 'Longshan Temple', '1'),
    xm_dragonDoor: (c, r) => {
      const x = r.x + r.w / 2, y = r.y + r.h;
      shadow(c, x, y, 7, 1.8);
      rect(c, '#55606a', x - 0.6, y - 14, 1.2, 14);
      block(c, x - 9, y - 22, 18, 9, '#2a8a5a', '#3aa06a');
      txt(c, '入口 ➜', x, y - 19.4, 3.4, '#ffffff', 900);
      txt(c, 'ENTRANCE', x, y - 15.6, 2.2, '#e8ffe8', 700);
    },
    xm_waterfall: (c, r) => {
      const y = r.y + r.h;
      for (let i = r.x; i <= r.x + r.w; i += 8) block(c, i - 1.4, y - 7, 2.8, 7, '#9a968e', '#b8b4aa');
      rect(c, '#b8b4aa', r.x - 1, y - 7, r.w + 2, 1.6);
    },
    xm_burner: burner,
    xm_offer: (c, r, t) => offerTable(c, r.x + r.w / 2, r.y + r.h, r.w, t),
    xm_moon: moonTable,
    xm_sticks: sticks,
    xm_herbTea: herbCounter,
    xm_herbSign: (c, r) => herbSign(c, r),
    xm_bopiliao: (c, r) => historyBoard(c, r),
    xm_hxNoodle: (c, r, t) => hxStall(c, r, t, '擔仔麵', '#c0392b', 'noodle'),
    xm_hxSoup: (c, r, t) => hxStall(c, r, t, '四神湯', '#b8862a', 'noodle'),
    xm_chess: (c, r, t) => chessTable(c, r.x + r.w / 2, r.y + r.h, t, true),
    xm_pigeons: pigeons,
    xm_canoe: (c, r) => canoe(c, r),
    xm_fortune: fortuneTable,
    xm_oldIce: oldIce,
  },
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'xm_shop':
        shopFacade(c, d);
        if (SHOPS[XM_SHOPS[d.variant ?? 0]].style === 'cinema') marquee(c, d, t);
        return true;
      case 'xm_vsign':
        vsign(c, d, t);
        return true;
      case 'xm_screen':
        screen(c, d, t);
        return true;
      case 'xm_person':
        person(c, d, t);
        return true;
      case 'xm_graffiti':
        graffiti(c, d);
        return true;
      case 'xm_ramp':
        ramp(c, d);
        return true;
      case 'xm_reel':
        reel(c, d, t);
        return true;
      case 'xm_bench':
        bench(c, d);
        return true;
      case 'xm_aboard':
        aboard(c, d);
        return true;
      case 'xm_balloons':
        balloons(c, d, t);
        return true;
      case 'xm_streetSign':
        streetSign(c, d);
        return true;
      case 'xm_standTable':
        standTable(c, d);
        return true;
      case 'xm_ubike':
        ubike(c, d);
        return true;
      case 'xm_traffic':
        traffic(c, d, t);
        return true;
      case 'xm_rainbow':
        rainbow(c, d);
        return true;
      case 'xm_rail':
        railing(c, d);
        return true;
      case 'xm_busStop':
        busStop(c, d);
        return true;
      case 'xm_plaque':
        plaqueArt(c, d.x, d.y, d.variant ?? 0);
        return true;
      case 'xm_redHouse':
        redHouse(c, d);
        return true;
      case 'xm_crossHall':
        crossHall(c, d);
        return true;
      case 'xm_billboard':
        billboard(c, d);
        return true;
      case 'xm_cafe':
        cafeTable(c, d);
        return true;
      case 'xm_redSign':
        redSign(c, d);
        return true;
      case 'xm_mainHall':
        mainHall(c, d, t);
        return true;
      case 'xm_frontHall':
        frontHall(c, d, t);
        return true;
      case 'xm_tower':
        tower(c, d, t);
        return true;
      case 'xm_lanterns':
        lanternString(c, d, t);
        return true;
      case 'xm_offerTable':
        offerTable(c, d.x, d.y, 44, t);
        return true;
      case 'xm_slips':
        slips(c, d);
        return true;
      case 'xm_cushion':
        cushion(c, d);
        return true;
      case 'xm_rockery':
        rockery(c, d, t);
        return true;
      case 'xm_koi':
        koi(c, d, t);
        return true;
      case 'xm_stoneSign':
        stoneSign(c, d);
        return true;
      case 'xm_incenseStall':
        incenseStall(c, d);
        return true;
      case 'xm_herbPot':
        herbPot(c, d, t);
        return true;
      case 'xm_huaxiGate':
        huaxiGate(c, d, t);
        return true;
      case 'xm_stall':
        huaxiStall(c, d, t);
        return true;
      case 'xm_oldLamp':
        oldLamp(c, d);
        return true;
      case 'xm_rickshaw':
        rickshaw(c, d);
        return true;
      case 'xm_chessTable':
        chessTable(c, d.x, d.y, t + (d.variant ?? 0), true);
        return true;
      case 'xm_banyan':
        banyan(c, d, t);
        return true;
      case 'xm_toilet':
        toilet(c, d);
        return true;
      default:
        return false;
    }
  },
  animatedObjects: ['xm_meesua', 'xm_chicken', 'xm_tea', 'xm_claw', 'xm_busker', 'xm_sticker', 'xm_burner', 'xm_sticks', 'xm_pigeons', 'xm_hxNoodle', 'xm_hxSoup', 'xm_chess', 'xm_market', 'xm_clothes'],
  animatedDecor: ['xm_screen', 'xm_traffic', 'xm_rockery', 'xm_koi', 'xm_lanterns', 'xm_balloons', 'xm_reel', 'xm_vsign', 'xm_frontHall', 'xm_mainHall'],
});
